import { create } from 'zustand'
import { resolveUrl, toBlobUrl, isBlobUrl, blobFetch } from '../config'
import type { OrbitEntry } from '../utils/orbitNavigation'

/**
 * ⚠️  APPSTORE SIZE WARNING
 * 
 * This file is 2741+ lines. DO NOT add new functions here.
 * 
 * Before adding code, create it in:
 * - utils/ for utilities (manifestLoader.ts, section3dLoader.ts, etc.)
 * - services/ for business logic (DirectHitStrategy.ts, etc.)
 * - hooks/ for custom hooks (useShopCardPreview.ts, etc.)
 * 
 * appStore should only contain:
 * 1. State interface definitions
 * 2. Store creation with Zustand
 * 3. Action handlers that call imported utilities
 * 
 * Keep appStore focused and delegate to utilities!
 */

import { Camera, Point3D } from '../types/colmap'
import { Dataset } from '../types/dataset'
import { buildPointCloudMappings, type PointCloudMappings } from '../utils/pointCloudMappings'
import { createPointKDTree, createCameraKDTree, type KDTree } from '../utils/spatialIndex'
import { PixelCache } from '../utils/pixelCache'
import { OffsetMap, calculateDistance3D } from '../types/offsetMap'
import { buildConnectivityGraph, buildConnectivityGraphFromManifest, buildConnectivityGraphFromSection3D, buildSyntheticOffsetMapFromManifest } from '../utils/offsetMapUtils'
import { calculateVisibleFramesSimple } from '../utils/visibleFrames'
import { calculateFrameWorldPositions, calculateAnchoredFramePositions } from '../utils/worldSpace'
import type { FrameSelectionReason } from '../components/GestureViewer/FrameSelectionBadge'
import { DirectHitStrategy } from '../services/DirectHitStrategy'
import type { FrameNavigationStrategy, FrameSelectionResult } from '../types/frameNavigation'
// INTERMEDIATE: Section detection (temporary until Python exports sections)
import { detectSectionsFromOffsets, type Section } from '../utils/sectionDetection'
import { calculateFrameWorldPositionsWithSections } from '../utils/worldSpaceWithSections'
// Phase 2: Multi-Section Shop Support (v2.0)
import type { ShopMetadata } from '../types/shopMetadata'
// Navigation History & Playback
import { NavigationHistoryService, PlaybackService } from '@dukkan/shared-core/services/navigation'
import { NavigationStep, PlaybackState, NavigationHistory } from '@dukkan/shared-types/navigation'
// 🎬 Animation services
import { calculateFrameSequenceTiming, getAnimationConfig } from '../config/animationConfig'
import { getDoubleClickAnimationService } from '../services/frameAnimationService'
// Image cache
import { imageCache } from '../services/imageCache'
import { getFrameImagePath } from '../services/colmapLoader'
// Gzip-compressed section_3d loader
import { loadSection3D } from '../utils/section3dLoader'
import { buildNSEWWalkthroughPath } from '../utils/directionNavigation'
// Manifest loader with gzip support
import { loadManifestWithFallback } from '../utils/manifestLoader'
// Minimap types
import type { MinimapNavData } from '../types/MinimapTypes'

/**
 * Fetch wrapper that includes auth headers when a token is available.
 * Used for scan-data proxy requests that need ownership verification.
 * Blob-aware: for direct blob URLs, appends SAS token and skips auth headers.
 */
function authFetch(url: string, init?: RequestInit): Promise<Response> {
  // Direct blob URL: use SAS token, skip auth header
  if (isBlobUrl(url)) {
    return blobFetch(url, init)
  }
  const authToken = localStorage.getItem('dukkan_token')
  if (authToken) {
    const headers = new Headers(init?.headers)
    if (!headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${authToken}`)
    }
    return fetch(url, { ...init, headers })
  }
  return fetch(url, init)
}

export interface AppSettings {
  transitionDuration: number // milliseconds (200-800)
  gestureSensitivity: 'low' | 'medium' | 'high'
  debugMode: boolean
  raycastMode: 'projection' | 'pixels' | 'auto' // Phase 2: Pixel optimization A/B testing
  enableFrameBlur: boolean // Phase 5: Background frame blur for depth-of-field effect
  useDistanceCoverage: boolean // Phase 5: Use Euclidean distance instead of viewport intersection for coverage
  enableDzFilter: boolean // Phase 1: Filter out frames with large depth changes during pan navigation
  useDirectionalPan: boolean // Phase 2.3: Use 3D cone-based directional pan (true) vs viewport-based pan (false)
  useOldPanBehavior: boolean // Stage 2: Toggle between old coverage-based (true) vs new Stage 2 intelligent selection (false)
  enableNewFrameSelection: boolean // New: Use new frame selection flow (double-click + manual) instead of auto pan-based winner selection
  max3DDistance: number // Max 3D Euclidean distance for connected frames
  maxDz: number // Max vertical (Z-axis) distance for connected frames
}

export interface AppState {
  // Dataset selection
  availableDatasets: Dataset[]
  currentDatasetId: string | null
  isLoadingDatasets: boolean
  
  // Shop selection (legacy - will be replaced by datasets)
  currentShopId: string | null
  lastSelectedShop: string | null // Last shop user visited (for back button navigation)
  currentSectionFramesPath: string | null // Path to current section's frames directory
  availableShops: string[]
  
  // COLMAP data
  cameras: Camera[]
  missingFrames: Set<string> // frame_file names (e.g. "frame_0042.jpg") that returned 404
  points3D: Point3D[]
  isLoadingData: boolean
  canUseTapIndicator: boolean // Two-stage loading: true when 3D data is ready, false during loading
  loadingProgress: number // 0-100 percentage (tracks reconstruction loading stages)
  loadingStage: string // Current stage name (e.g., "Loading cameras...", "Building spatial structures...")
  dataError: string | null
  
  // Warm-start frame for eliminating black screen on navigation
  warmStartFrame: string | null // frame_0000.jpg URL shown while manifest loads
  
  // Warm-start frame for eliminating black screen on navigation
  warmStartFrame: string | null // frame_0000.jpg URL shown while manifest loads
  
  // Spatial data structures (Phase 2.1)
  pointCloudMappings: PointCloudMappings | null
  pointKDTree: KDTree | null
  cameraKDTree: KDTree | null
  
  // Phase 2: Pixel-based raycast data (v2.0 format)
  hasPixelData: boolean // True if current dataset has v2.0 pixel coordinates
  pixelCache: Map<number, [number, number]> | null // point_id -> [x, y] for current camera
  pixelCacheStats: { hits: number; misses: number; buildTime: number; cacheSize: number; lastRebuild: number } | null
  lastCachedCameraId: number | null // Track which camera the cache is for (avoid unnecessary rebuilds)
  
  // Navigation state
  currentCameraIndex: number
  isTransitioning: boolean
  playingThumbnailIndex: number | null // Index in thumbnail list for highlighting only (no navigation)
  
  // Pan & Zoom state (Phase 2.3: Pan/Zoom Navigation)
  panOffset: { x: number; y: number } // Current pan displacement in pixels
  zoomScale: number // Current zoom level (1.0 to 3.0)
  preferredDiscoveryRadiusPx: number | null // User's last adjusted discovery radius (persists across taps)
  isPanning: boolean // Touch is currently active
  isZooming: boolean // Wheel zoom is currently active
  
  // Multi-Frame Viewport (Phase 2.6: Multi-Frame Rendering)
  visibleCameraIndices: number[] // All frames currently visible in viewport
  visibleCameraOffsets: Map<number, { x: number; y: number }> // Frame index → offset from current
  
  // Legacy multi-camera pan (DEPRECATED - will be removed after Phase 2.6)
  nextCameraIndex: number | null // Camera to transition to during pan gesture
  stitchOffset: { x: number; y: number } // Calculated pixel shift from point correspondences ({0,0} = fallback)
  // isMultiFrameJump removed - only use COLMAP connectivity
  
  // Phase 2.5: Pre-calculated Offset Map
  offsetMap: OffsetMap | null         // Loaded offset map (dx, dy, dz for all frame pairs)
  hasOffsetMap: boolean                // True if offset_map.json loaded successfully
  offsetMapLoadTime: number | null     // Load time in ms (for performance tracking)
  offsetMaps: Map<number, OffsetMap> | null // Per-section offset maps (sectionId → OffsetMap) for multi-section shops
  connectivityGraph: Map<number, Set<number>> | null // Cached connectivity graph (built once from offsetMap)
  extendedConnectivityGraph: Map<number, Set<number>> | null // Extended connectivity: ALL reachable frames via any path (for context frames)
  
  // 🆕 Phase 1: Fragmented Data - Manifest
  manifest: any | null // Loaded manifest.json (frames_index, camera_models, frames with neighbor offsets)
  hasManifest: boolean // True if manifest.json loaded successfully
  
  // Minimap navigation data (loaded from minimap_nav.json)
  minimapNavData: MinimapNavData | null // Camera path, structure lines, corridor directions
  corridorDirectionByFrame: Map<number, number> | null // Frame index → corridor direction in radians (derived from minimapNavData)

  // Pan mode toggle (pan-frame-selection on/off)
  panModeEnabled: boolean // When true, panning selects frames; when false, only arrow buttons navigate
  exploreMode: boolean // When true, single-finger pan raycasts nearest 3D point and orbits/walks through frames

  // Tap indicator → minimap bridge (Steps 11-12)
  tapPoint3D: [number, number, number] | null // 3D world point tapped by user (for minimap projection)
  tapDiscoveryFrameNumbers: Set<number> | null // Frame numbers of cameras that see the tapped 3D point
  
  // Phase 2.7: Position-Based Navigation (World Space)
  frameWorldPositions: Array<{ frameIndex: number; worldX: number; worldY: number }> // Absolute positions of all frames (cumulative from frame 0)
  anchoredFramePositions: Array<{ frameIndex: number; worldX: number; worldY: number }> // Positions relative to selected frame (re-calculated on selection)
  viewportWorldPosition: { x: number; y: number } // Viewport position in world space
  viewportSize: { width: number; height: number } // Viewport dimensions (canvas size)
  viewportVersion: number // Increments on viewport changes to force re-renders
  skipAutoSelectNextViewportChange?: boolean // 🎬 During animation: skip auto-select when viewport changes (prevents intermediate frame selection)
  
  // INTERMEDIATE: Multi-Section Support (temporary until Python exports sections)
  sections: Section[] // Detected disconnected sections
  currentSectionId: number // Currently active section
  isDrawerOpen: boolean // Sections drawer visibility
  
  // Phase 2: Multi-Section Shop Support (v2.0)
  shopMetadata: ShopMetadata | null // Loaded shop_metadata.json for v2.0 multi-section shops
  isMultiSection: boolean // True if current shop is v2.0 multi-section format
  loadedSectionIds: Set<number> // Track which sections have been lazy-loaded
  
  // Phase 3: Multi-Dataset Section Navigation (user sections → COLMAP segments)
  shopDatasets: Dataset[] // All datasets for current shop, deduped by sectionId (latest version wins)
  activeUserSectionId: string | null // Which user-defined section is currently loaded (e.g. "sec_abc12345")
  
  // Frame Selection Tracking (Phase 2.8: Debug visualization)
  currentFrameSelection: FrameSelectionReason | null // Current winner frame selection
  previousFrameSelection: FrameSelectionReason | null // Previous winner (for comparison)
  frameSelectionHistory: FrameSelectionReason[] // Full history of selections
  showFrameSelectionBadge: boolean // Enable/disable the debug badge
  
  // NEW MODE: Manual Frame Selection (double-click in NEW MODE)
  manuallySelectedFrameIndex: number | null // Frame selected by user double-click in NEW MODE
  lastManualSelectionTime: number // Timestamp when user manually selected a frame (for preventing auto-selection override)
  
  // 🎬 Frame Animation (centralized state for animations)
  animatingFrameIndex: number | null // Current frame being animated (null when not animating)
  animationTrigger: 'PAN_MOVE_PREVIEW' | 'PAN_END_SMOOTH' | 'DOUBLE_CLICK_ANIMATE' | 'SECTION_PLAY_ALL' | null // Type of animation for styling
  
  // 🎬 Frame sequence animation (blocks all user input during animation)
  isFrameSequenceAnimating: boolean // True while frame sequence animation is running
  frameSequenceAnimationCancel: (() => void) | null // Function to cancel animation (for gesture/Escape key)
  frameSequenceAnimationTarget: number | null // Target frame for current frame sequence animation
  
  // Frame Navigation Strategy (Pan-based frame navigation)
  frameNavigationStrategy: FrameNavigationStrategy | null // Current navigation strategy (Direct Hit, etc.)
  lastNavigationResult: FrameSelectionResult | null // Result from last pan gesture
  originBoundarySize: { width: number; height: number } // Frame dimensions for origin boundary
  panFailureDirection: 'up' | 'down' | 'left' | 'right' | null // Direction of blocked pan for animation
  availableDirections: ('north' | 'south' | 'east' | 'west')[] // Directions that have frames available for navigation
  arrowCandidateFrames: number[] // Best candidate frame index per direction (N/S/E/W) for preloading

  // Orbit mode (orbit around tapped 3D point)
  orbitSequence: OrbitEntry[] | null // Sorted cameras for orbiting, null when inactive
  orbitIndex: number // Current position in orbit sequence
  orbitTappedDepth: number // Depth at the originally tapped camera (for zoom compensation)
  orbitPoint3D: [number, number, number] | null // 3D point being orbited
  orbitCircleRadiusMeters: number // Sphere radius in meters for consistent sizing
  
  // Candidate Frames (for bottom scrollable list)
  candidateFrameIndices: number[] // Frame indices visible/filtered in viewport
  
  // UI state
  menuOpen: boolean
  showDebugOverlay: boolean
  showMeshViewer: boolean
  currentMeshSection: number  // 0-15 for SMART_SECTIONS, or 0 for single section
  controlsVisible: boolean
  showNavigationIndicators: boolean // Production-style nav dots visible when controls are showing
  trajectoryOverlayVisible: boolean // Camera trajectory overlay visibility (false = hidden, true = show faded/visible)
  highlightedZoneDirection: 'north' | 'south' | 'east' | 'west' | null // Arrow direction whose zone cameras are highlighted in yellow
  
  // Item marker label visibility (dots-only mode)
  markerLabelsVisible: boolean // When false, hide flag labels — only show circles
  toggleMarkerLabels: () => void

  // 🎯 Overlay Persistence State (Feature 2: Unified Dismiss)
  // NOTE: Navigation hints are DECOUPLED from overlays - always visible, managed separately
  frameBalloonVisible: boolean // Frame relationship balloon info
  navigationSettingsBalloonVisible: boolean // Navigation settings balloon
  
  is3DMode: boolean // True when 3D viewer is active
  
  // Settings
  settings: AppSettings
  
  // Navigation History & Playback (merged from navigationSlice)
  navigationHistory: NavigationHistory
  // Flag to prevent recording navigation steps during history navigation
  isNavigatingHistory: boolean
  playback: PlaybackState
  
  // Actions
  setIsLoadingDatasets: (loading: boolean) => void
  setAvailableDatasets: (datasets: Dataset[]) => void
  setCurrentDataset: (datasetId: string) => void
  loadShopMetadata: (dataset: Dataset) => Promise<ShopMetadata | null>
  loadSection: (sectionId: number) => Promise<void>
  loadDataset: (datasetId: string, options?: { section?: number; frame?: number; skipAnimation?: boolean }) => Promise<void>
  setLoadingProgress: (progress: number, stage: string) => void
  setCurrentShop: (shopId: string) => void
  setLastSelectedShop: (shopId: string | null) => void
  setSelectedShop: (shopId: string | null) => void
  setAvailableShops: (shops: string[]) => void
  setCOLMAPData: (cameras: Camera[], points: Point3D[]) => void
  markFrameMissing: (frameFile: string) => void
  setLoadingData: (loading: boolean) => void
  loadFromDirectUrl: (camerasUrl: string, base: string, sas: string) => Promise<void>
  setDataError: (error: string | null) => void
  setWarmStartFrame: (frame: string | null) => void
  setMinimapNavData: (data: MinimapNavData | null) => void
  setTapMinimapData: (point3D: [number, number, number] | null, projectionCameraIndices: number[] | null) => void
  setPanModeEnabled: (enabled: boolean) => void
  setExploreMode: (enabled: boolean) => void
  navigateToCamera: (index: number) => void
  navigateNext: () => void
  navigatePrevious: () => void
  setTransitioning: (transitioning: boolean) => void
  toggleMenu: () => void
  setMenuOpen: (open: boolean) => void
  toggleDebugOverlay: () => void
  toggleMeshViewer: () => void
  setCurrentMeshSection: (section: number) => void
  setControlsVisible: (visible: boolean) => void
  setShowNavigationIndicators: (visible: boolean) => void
  showControls: () => void
  hideControls: () => void
  updateSettings: (settings: Partial<AppSettings>) => void
  setPanOffset: (x: number, y: number) => void
  setZoomScale: (scale: number) => void
  setPreferredDiscoveryRadiusPx: (radiusPx: number | null) => void
  setIsPanning: (panning: boolean) => void
  setIsZooming: (zooming: boolean) => void
  
  // Multi-Frame Viewport (Phase 2.6)
  setVisibleCameras: (indices: number[], offsets: Map<number, { x: number; y: number }>) => void
  
  // Phase 2.7: Position-Based Navigation (World Space)
  setViewportPosition: (x: number, y: number) => void
  panViewport: (deltaX: number, deltaY: number) => void
  // Atomic zoom + viewport update (prevents intermediate renders)
  setZoomAndViewport: (scale: number, x: number, y: number) => void
  centerViewportOnFrame: (frameIndex: number, displayWidth?: number, displayHeight?: number, options?: { skipAutoSelect?: boolean }) => void
  selectFrameWithNeighbors: (frameIndex: number, displayWidth?: number, displayHeight?: number) => void // New: Select frame and center viewport (independent from pan)
  selectFrameWithoutPan: (frameIndex: number) => void // Select frame WITHOUT changing viewport or zoom
  setViewportSize: (width: number, height: number) => void
  
  // Legacy (DEPRECATED)
  setNextCameraIndex: (index: number | null) => void // Phase 2.3.5
  setStitchOffset: (offsetX: number, offsetY: number) => void // Phase 2.3.5: Set calculated stitch offset (X and Y)
  
  // Frame Selection Tracking (Phase 2.8)
  recordFrameSelection: (selection: FrameSelectionReason) => void
  toggleFrameSelectionBadge: () => void
  setShowFrameSelectionBadge: (show: boolean) => void
  clearFrameSelectionHistory: () => void
  
  // Candidate Frames (for bottom scrollable list)
  setCandidateFrames: (indices: number[]) => void
  
  // NEW MODE: Manual Frame Selection
  setManuallySelectedFrame: (frameIndex: number | null) => void
  setAnimatingFrameIndex: (frameIndex: number | null) => void
  setAnimationTrigger: (trigger: 'PAN_MOVE_PREVIEW' | 'PAN_END_SMOOTH' | 'DOUBLE_CLICK_ANIMATE' | 'SECTION_PLAY_ALL' | null) => void
  
  // 🎬 Frame sequence animation actions
  setFrameSequenceAnimating: (isAnimating: boolean) => void
  setFrameSequenceAnimationCancel: (cancelFn: (() => void) | null) => void
  setFrameSequenceAnimationTarget: (targetFrame: number | null) => void
  cancelFrameSequenceAnimation: () => void
  playAllFramesInSection: () => Promise<void>
  
  // Camera trajectory overlay visibility
  showTrajectoryOverlay: () => void
  setTrajectoryOverlayVisible: (visible: boolean) => void
  setHighlightedZoneDirection: (direction: 'north' | 'south' | 'east' | 'west' | null) => void
  
  // 🎯 Overlay Persistence Actions (Feature 2: Unified Dismiss)
  // NOTE: Navigation hints are DECOUPLED - no setter actions needed
  setFrameBalloonVisible: (visible: boolean) => void
  setNavigationSettingsBalloonVisible: (visible: boolean) => void
  dismissAllOverlays: () => void // Dismiss all overlays (hints remain always visible)
  
  // Frame Navigation Strategy (Pan-based navigation)
  initializeFrameNavigationStrategy: (frameWidth: number, frameHeight: number) => void
  updateFrameNavigation: (deltaPanX: number, deltaPanY: number, panDirection: 'up' | 'down' | 'left' | 'right' | null) => void
  resetFrameNavigationStrategy: () => void
  setPanFailureDirection: (direction: 'up' | 'down' | 'left' | 'right' | null) => void
  setAvailableDirections: (directions: ('north' | 'south' | 'east' | 'west')[]) => void
  setArrowCandidateFrames: (frames: number[]) => void
  setOrbitSequence: (sequence: OrbitEntry[] | null, tappedDepth?: number, point3D?: [number, number, number] | null, circleRadiusMeters?: number) => void
  setOrbitIndex: (index: number) => void
  clearOrbit: () => void
  
  resetPanZoom: () => void
  reset: () => void
  
  // INTERMEDIATE: Multi-Section Support
  _loadSectionData: (sectionPaths: { cameras: string; points3d: string; offset_map: string; frames: string }, sectionId: number) => Promise<any>
  loadSectionAsIndependentShop: (shopId: string, sectionId: number) => Promise<void>
  switchToSection: (sectionId: number, options?: { skipAnimation?: boolean }) => Promise<void>
  toggleSectionsDrawer: () => void
  // Load a different user section's dataset. `options` is passed straight to loadDataset.
  loadSectionDataset: (
    userSectionId: string,
    options?: { section?: number; frame?: number; skipAnimation?: boolean },
  ) => Promise<void>
  
  // Navigation History & Playback actions
  recordNavigationStep: (step: NavigationStep) => void
  goBackInHistory: () => void
  goForwardInHistory: () => void
  canGoBack: () => boolean
  canGoForward: () => boolean
  clearHistory: () => void
  startPlayback: (mode: 'sequence' | 'random') => void
  stopPlayback: () => void
  setPlaybackSpeed: (speed: number) => void

  // Image dimensions for navigation logic
  imageDimensions: { imageWidth: number; imageHeight: number; displayWidth: number; displayHeight: number } | null
  setImageDimensions: (dimensions: { imageWidth: number; imageHeight: number; displayWidth: number; displayHeight: number } | null) => void
}

const defaultSettings: AppSettings = {
  transitionDuration: 400,
  gestureSensitivity: 'medium',
  debugMode: false,
  raycastMode: 'projection', // Default to current projection method (safe, proven)
  enableFrameBlur: true, // Enable blur by default for better visual hierarchy
  useDistanceCoverage: true, // Default to distance-based (simpler, faster)
  enableDzFilter: true, // Enable by default to prevent disorienting scene jumps
  useDirectionalPan: true, // Phase 2.3: TESTING - Enable cone-based directional pan
  useOldPanBehavior: false, // Stage 2: Default to new intelligent selection (false = use Stage 2)
  enableNewFrameSelection: true, // New: Enable new frame selection flow by default
  max3DDistance: 1.7, // Default max 3D distance
  maxDz: 1, // Default max dZ distance
}

/**
 * Helper: Rebuild pixel cache for a camera (Phase 2)
 */
function rebuildPixelCache(
  camera: Camera,
  hasPixelData: boolean,
  raycastMode: 'projection' | 'pixels' | 'auto',
  currentCache: Map<number, [number, number]> | null,
  lastCachedCameraId: number | null
): { cache: Map<number, [number, number]> | null; stats: any; cameraId: number | null } {
  if (!hasPixelData || raycastMode === 'projection') {
    return { cache: null, stats: null, cameraId: null }
  }
  
  // Optimization: Skip rebuild if cache is already for this camera
  if (currentCache && lastCachedCameraId === camera.id) {
    console.log(`✅ [rebuildPixelCache] Cache already valid for camera ${camera.id}, skipping rebuild (OPTIMIZATION)`)
    return { cache: currentCache, stats: null, cameraId: camera.id }
  }
  
  console.log(`🔨 [rebuildPixelCache] Building new cache for camera ${camera.id}...`)
  
  try {
    const buildStart = performance.now()
    const pixelCache = new PixelCache()
    const success = pixelCache.buildFromCamera(camera)
    const buildTime = performance.now() - buildStart
    
    if (success) {
      const stats = pixelCache.getStats()
      console.log(`✅ [rebuildPixelCache] Success: ${stats.cacheSize} points in ${buildTime.toFixed(2)}ms`)
      return {
        cache: pixelCache.getCache(),
        stats: stats,
        cameraId: camera.id
      }
    } else {
      console.warn(`⚠️  [rebuildPixelCache] Build failed for camera ${camera.id}`)
    }
  } catch (error) {
    console.error('❌ [rebuildPixelCache] Error building cache:', error)
    // Gracefully degrade - return null cache
  }
  
  return { cache: null, stats: null, cameraId: null }
}

export const useAppStore = create<AppState>((set, get) => ({
  // Initial state
  availableDatasets: [],
  currentDatasetId: null,
  isLoadingDatasets: false,
  currentShopId: null,
  lastSelectedShop: null,
  currentSectionFramesPath: null,
  availableShops: [],
  cameras: [],
  missingFrames: new Set<string>(),
  points3D: [],
  isLoadingData: false,
  canUseTapIndicator: false,
  loadingProgress: 0,
  loadingStage: '',
  dataError: null,
  warmStartFrame: null,
  pointCloudMappings: null,
  pointKDTree: null,
  cameraKDTree: null,
  hasPixelData: false,
  pixelCache: null,
  pixelCacheStats: null,
  lastCachedCameraId: null,
  currentCameraIndex: 0,
  isTransitioning: false,
  panOffset: { x: 0, y: 0 },
  zoomScale: 1.0,
  preferredDiscoveryRadiusPx: null,
  isPanning: false,
  isZooming: false,
  
  // Multi-Frame Viewport (Phase 2.6)
  visibleCameraIndices: [0], // Initially just the first frame
  visibleCameraOffsets: new Map([[0, { x: 0, y: 0 }]]), // Frame 0 at origin
  
  // Legacy (DEPRECATED)
  nextCameraIndex: null, // Phase 2.3.5: No next camera initially
  stitchOffset: { x: 0, y: 0 }, // Phase 2.3.5: Calculated from point correspondences ({0,0} = use fallback)
  // isMultiFrameJump removed
  
  // Phase 2.5: Offset Map
  offsetMap: null,
  hasOffsetMap: false,
  offsetMapLoadTime: null,
  offsetMaps: null,
  connectivityGraph: null,
  extendedConnectivityGraph: null,
  
  // 🆕 Phase 1: Fragmented Data - Manifest
  manifest: null,
  hasManifest: false,
  
  // Minimap navigation data
  minimapNavData: null,
  corridorDirectionByFrame: null,
  panModeEnabled: false,
  exploreMode: false,
  tapPoint3D: null,
  tapDiscoveryFrameNumbers: null,
  
  // Phase 2.7: Position-Based Navigation (World Space)
  frameWorldPositions: [],
  anchoredFramePositions: [],
  viewportWorldPosition: { x: 0, y: 0 },
  viewportSize: { width: 800, height: 600 },
  viewportVersion: 0, // Default canvas size
  
  // INTERMEDIATE: Multi-Section Support
  sections: [],
  currentSectionId: 0,
  isDrawerOpen: false,
  
  // Phase 2: Multi-Section Shop Support (v2.0)
  shopMetadata: null,
  isMultiSection: false,
  loadedSectionIds: new Set([0]), // Root section (section_id 0) is loaded by default
  
  // Phase 3: Multi-Dataset Section Navigation
  shopDatasets: [],
  activeUserSectionId: null,
  
  // Frame Selection Tracking (Phase 2.8)
  currentFrameSelection: null,
  previousFrameSelection: null,
  frameSelectionHistory: [],
  showFrameSelectionBadge: true, // Show badge by default in debug mode
  
  // NEW MODE: Manual Frame Selection (double-click in NEW MODE)
  manuallySelectedFrameIndex: null,
  lastManualSelectionTime: 0,
  
  // Camera trajectory overlay visibility
  trajectoryOverlayVisible: false, // Start hidden, show with button
  trajectoryHideTimer: null as NodeJS.Timeout | null,
  highlightedZoneDirection: null, // No zone highlighted initially
  
  // Item marker label visibility
  markerLabelsVisible: true, // Show labels by default

  // 🎯 Overlay Persistence State (Feature 2: Unified Dismiss)
  // NOTE: Navigation hints are DECOUPLED - always visible
  frameBalloonVisible: false, // Balloon starts hidden
  navigationSettingsBalloonVisible: false, // Settings balloon starts hidden
  
  // 🎬 Frame Animation (centralized state for animations)
  animatingFrameIndex: null,
  animationTrigger: null,
  
  // 🎬 Frame sequence animation (blocks all user input)
  isFrameSequenceAnimating: false,
  frameSequenceAnimationCancel: null,
  frameSequenceAnimationTarget: null,
  
  // Frame Navigation Strategy (Pan-based frame navigation)
  frameNavigationStrategy: null,
  lastNavigationResult: null,
  originBoundarySize: { width: 0, height: 0 },
  panFailureDirection: null,
  availableDirections: ['north', 'south', 'east', 'west'] as ('north' | 'south' | 'east' | 'west')[],
  arrowCandidateFrames: [] as number[],

  // Orbit mode
  orbitSequence: null as OrbitEntry[] | null,
  orbitIndex: 0,
  orbitTappedDepth: 1.0,
  orbitPoint3D: null as [number, number, number] | null,
  orbitCircleRadiusMeters: 0,
  
  // Candidate Frames (for bottom scrollable list)
  candidateFrameIndices: [],
  
  menuOpen: false,
  toggleMarkerLabels: () => set((s) => ({ markerLabelsVisible: !s.markerLabelsVisible })),
  showDebugOverlay: false,
  showMeshViewer: false,
  currentMeshSection: 0,
  controlsVisible: false,
  showNavigationIndicators: false,
  is3DMode: false,
  settings: defaultSettings,
  
  // Navigation History & Playback (merged from navigationSlice)
  navigationHistory: { steps: [], currentIndex: -1 },
  isNavigatingHistory: false,
  playback: { isPlaying: false, mode: null, speed: 1.0, intervalId: null },
  playingThumbnailIndex: null,
  
  // Image dimensions for navigation logic
  imageDimensions: null,
  
  // Navigation History & Playback actions
  recordNavigationStep: (step) => {
    const history = get().navigationHistory;
    const newHistory = NavigationHistoryService.recordStep(history, step);
    console.log(`📚 [recordNavigationStep] History updated: ${history.steps.length} → ${newHistory.steps.length} steps, currentIndex: ${newHistory.currentIndex}`);
    set({ navigationHistory: newHistory });
  },
  
  goBackInHistory: async () => {
    const history = get().navigationHistory;
    const result = NavigationHistoryService.goBack(history);
    
    if (result.targetIndex !== null) {
      const targetStep = history.steps[history.currentIndex - 1];
      
      // Cancel any running animation first
      get().cancelFrameSequenceAnimation();
      
      // Switch to target section if different from current
      const currentSectionId = get().currentSectionId;
      const targetSectionId = targetStep.sectionId ?? 0;
      
      if (targetSectionId !== currentSectionId) {
        console.log(`🔄 [History] Switching from section ${currentSectionId} to section ${targetSectionId}`);
        const isMultiSection = get().isMultiSection;
        
        if (isMultiSection) {
          await get().switchToSection(targetSectionId);
          set({ navigationHistory: result.history, isNavigatingHistory: true });
          // Don't return - continue to animation logic below
        }
      } else {
        // Same section - just update history state
        set({ navigationHistory: result.history, isNavigatingHistory: true });
      }
      
      // Calculate timing for journey animation from current position to target
      const currentFrame = get().manuallySelectedFrameIndex ?? 0;
      const timing = calculateFrameSequenceTiming(currentFrame, targetStep.endFrame);
      const animConfig = getAnimationConfig('DOUBLE_CLICK_ANIMATE');
      
      if (!animConfig) {
        // Fallback to instant jump if no animation config
        get().selectFrameWithoutPan(result.targetIndex);
        setTimeout(() => set({ isNavigatingHistory: false }), 0);
        return;
      }
      
      // Start animated journey from current frame to target
      set({ 
        isFrameSequenceAnimating: true,
        frameSequenceAnimationTarget: targetStep.endFrame
      });
      
      console.log(`⬅️ [History] Animating back from ${currentFrame} to ${targetStep.endFrame} (section ${targetSectionId}, ${timing.framesToShow.length} frames, ${timing.totalDuration}ms)`);
      
      const animationService = getDoubleClickAnimationService();
      const cancelFn = animationService.animateFrameSequence(
        timing.framesToShow,
        targetStep.endFrame,
        { ...animConfig, frameDuration: timing.frameDuration },
        {
          onFrameShow: (frameIndex: number) => {
            get().selectFrameWithoutPan(frameIndex);
            get().centerViewportOnFrame(frameIndex);
          },
          onComplete: () => {
            const finalFrame = targetStep.endFrame;
            console.log(`✅ [History] Back animation complete, selecting final frame: ${finalFrame}`);
            set({ 
              isFrameSequenceAnimating: false,
              frameSequenceAnimationCancel: null,
              frameSequenceAnimationTarget: null
            });
            // Select final frame while isNavigatingHistory is still true (prevents recording)
            get().setManuallySelectedFrame(finalFrame);
            get().centerViewportOnFrame(finalFrame);
            // NOW clear the flag
            set({ isNavigatingHistory: false });
          }
        }
      );
      
      // Store cancel function for rapid clicks
      set({ frameSequenceAnimationCancel: cancelFn });
    }
  },
  
  goForwardInHistory: async () => {
    const history = get().navigationHistory;
    const result = NavigationHistoryService.goForward(history);
    
    if (result.targetIndex !== null) {
      const targetStep = history.steps[history.currentIndex + 1];
      
      // Cancel any running animation first
      get().cancelFrameSequenceAnimation();
      
      // Switch to target section if different from current
      const currentSectionId = get().currentSectionId;
      const targetSectionId = targetStep.sectionId ?? 0;
      
      if (targetSectionId !== currentSectionId) {
        console.log(`🔄 [History] Switching from section ${currentSectionId} to section ${targetSectionId}`);
        const isMultiSection = get().isMultiSection;
        
        if (isMultiSection) {
          await get().switchToSection(targetSectionId);
          set({ navigationHistory: result.history, isNavigatingHistory: true });
          // Don't return - continue to animation logic below
        }
      } else {
        // Same section - just update history state
        set({ navigationHistory: result.history, isNavigatingHistory: true });
      }
      
      // Calculate timing for journey animation from current position to target
      const currentFrame = get().manuallySelectedFrameIndex ?? 0;
      const timing = calculateFrameSequenceTiming(currentFrame, targetStep.endFrame);
      const animConfig = getAnimationConfig('DOUBLE_CLICK_ANIMATE');
      
      if (!animConfig) {
        // Fallback to instant jump if no animation config
        get().selectFrameWithoutPan(result.targetIndex);
        setTimeout(() => set({ isNavigatingHistory: false }), 0);
        return;
      }
      
      // Start animated journey from current frame to target
      set({ 
        isFrameSequenceAnimating: true,
        frameSequenceAnimationTarget: targetStep.endFrame
      });
      
      console.log(`➡️ [History] Animating forward from ${currentFrame} to ${targetStep.endFrame} (section ${targetSectionId}, ${timing.framesToShow.length} frames, ${timing.totalDuration}ms)`);
      
      const animationService = getDoubleClickAnimationService();
      const cancelFn = animationService.animateFrameSequence(
        timing.framesToShow,
        targetStep.endFrame,
        { ...animConfig, frameDuration: timing.frameDuration },
        {
          onFrameShow: (frameIndex: number) => {
            get().selectFrameWithoutPan(frameIndex);
            get().centerViewportOnFrame(frameIndex);
          },
          onComplete: () => {
            const finalFrame = targetStep.endFrame;
            console.log(`✅ [History] Forward animation complete, selecting final frame: ${finalFrame}`);
            set({ 
              isFrameSequenceAnimating: false,
              frameSequenceAnimationCancel: null,
              frameSequenceAnimationTarget: null
            });
            // Select final frame while isNavigatingHistory is still true (prevents recording)
            get().setManuallySelectedFrame(finalFrame);
            get().centerViewportOnFrame(finalFrame);
            // NOW clear the flag
            set({ isNavigatingHistory: false });
          }
        }
      );
      
      // Store cancel function for rapid clicks
      set({ frameSequenceAnimationCancel: cancelFn });
    }
  },
  
  canGoBack: () => {
    const history = get().navigationHistory;
    return history.currentIndex > 0;
  },
  
  canGoForward: () => {
    const history = get().navigationHistory;
    return history.currentIndex < history.steps.length - 1;
  },
  
  clearHistory: () => {
    set({ navigationHistory: { steps: [], currentIndex: -1 } });
  },
  
  startPlayback: (mode) => {
    const playback = get().playback;
    
    // Stop existing playback if any
    if (playback.intervalId) {
      clearInterval(playback.intervalId);
    }
    
    // Validate mode
    if (mode !== 'sequence' && mode !== 'random') {
      console.warn('[AppStore] Invalid playback mode:', mode);
      return;
    }
    
    const newPlayback: PlaybackState = {
      isPlaying: true,
      mode,
      speed: playback.speed,
      intervalId: null // Will be set by playback logic
    };
    
    set({ playback: newPlayback });
    console.log('Playback started:', mode);
  },
  
  stopPlayback: () => {
    const playback = get().playback;
    
    if (playback.intervalId) {
      clearInterval(playback.intervalId);
    }
    
    // Center viewport on the current frame when stopping playback
    const currentFrame = get().manuallySelectedFrameIndex ?? get().currentCameraIndex;
    if (currentFrame !== null && currentFrame >= 0) {
      get().centerViewportOnFrame(currentFrame);
    }
    
    set({
      playback: {
        ...playback,
        isPlaying: false,
        mode: null,
        intervalId: null
      }
    });
  },
  
  setPlayingThumbnailIndex: (index: number | null) => {
    set({ playingThumbnailIndex: index });
  },

  setPlaybackSpeed: (speed) => {
    // Validate speed
    const validSpeeds = [0.5, 1, 2, 4];
    if (!validSpeeds.includes(speed)) {
      console.warn('[AppStore] Invalid speed:', speed);
      return;
    }
    
    const playback = get().playback;
    set({ playback: { ...playback, speed } });
    
    // If currently playing, restart with new speed
    if (playback.isPlaying && playback.mode) {
      // This will trigger playback restart in the hook
      console.log('Speed changed to:', speed + 'x');
    }
  },
  
  // Actions
  setAvailableDatasets: (datasets) => {
    // Resolve all /api/ paths so they work in production (adds API_URL prefix)
    const resolved = datasets.map(d => ({
      ...d,
      paths: {
        ...d.paths,
        ...(d.paths.metadata && { metadata: resolveUrl(d.paths.metadata) }),
        cameras: resolveUrl(d.paths.cameras),
        points3d: resolveUrl(d.paths.points3d),
        points3d_simplified: resolveUrl(d.paths.points3d_simplified),
        frames: resolveUrl(d.paths.frames),
        ...(d.paths.root && { root: resolveUrl(d.paths.root) }),
        ...(d.paths.offset_map && { offset_map: resolveUrl(d.paths.offset_map) }),
        ...(d.paths.thumbnail && { thumbnail: resolveUrl(d.paths.thumbnail) }),
      },
    }))
    set({ availableDatasets: resolved })
  },
  
  setCurrentDataset: (datasetId) => set({ currentDatasetId: datasetId }),
  
  /**
   * Phase 2: Load shop_metadata.json for v2.0 multi-section shops
   * This replaces INTERMEDIATE gap-based section detection with explicit metadata
   */
  loadShopMetadata: async (dataset: Dataset): Promise<ShopMetadata | null> => {
    console.log(`📋 [loadShopMetadata] Checking for shop_metadata.json...`)
    
    // Check if dataset has metadata path (v2.0 multi-section format)
    if (!dataset.is_multi_section || !dataset.paths.metadata) {
      console.log(`📋 [loadShopMetadata] Legacy shop (no metadata) - will use gap detection`)
      return null
    }
    
    try {
      const metadataPath = dataset.paths.metadata
      console.log(`📋 [loadShopMetadata] Fetching metadata from: ${metadataPath}`)
      // Send auth headers so the proxy can verify ownership for private shops
      const authToken = localStorage.getItem('dukkan_token')
      const headers: Record<string, string> = {}
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`
      const response = await blobFetch(metadataPath, { headers })
      
      if (!response.ok) {
        console.warn(`⚠️  [loadShopMetadata] Failed to load metadata (${response.status}) - falling back to gap detection`)
        return null
      }
      
      const metadata = await response.json() as ShopMetadata
      
      // Resolve all section paths for production API URL
      for (const section of metadata.sections) {
        if (section.paths) {
          section.paths.cameras = resolveUrl(section.paths.cameras)
          section.paths.points3d = resolveUrl(section.paths.points3d)
          section.paths.offset_map = resolveUrl(section.paths.offset_map)
          section.paths.frames = resolveUrl(section.paths.frames)
          if (section.paths.manifest) section.paths.manifest = resolveUrl(section.paths.manifest)
          if (section.paths.metadata) section.paths.metadata = resolveUrl(section.paths.metadata)
          if (section.paths.thumbnail) section.paths.thumbnail = resolveUrl(section.paths.thumbnail)
        }
      }
      
      console.log(`✅ [loadShopMetadata] Loaded v${metadata.version} metadata: ${metadata.total_sections} section(s)`)
      console.log(`📋 [loadShopMetadata] Sections:`, metadata.sections.map(s => 
        `${s.name} (${s.frame_count} frames, coverage ${s.coverage_percent}%)`
      ))
      
      return metadata
    } catch (error) {
      console.error(`❌ [loadShopMetadata] Error loading metadata:`, error)
      return null
    }
  },
  
  /**
   * Phase 2: Lazy load a specific section's data (cameras, points, offset_map)
   * Only needed for non-root sections (section_id > 0)
   */
  loadSection: async (sectionId: number): Promise<void> => {
    const { shopMetadata, isMultiSection, loadedSectionIds } = get()
    
    console.log(`📂 [loadSection] Loading section ${sectionId}...`)
    
    // Validate multi-section shop
    if (!isMultiSection || !shopMetadata) {
      console.error(`❌ [loadSection] Not a multi-section shop - cannot load section ${sectionId}`)
      return
    }
    
    // Check if already loaded
    if (loadedSectionIds.has(sectionId)) {
      console.log(`✅ [loadSection] Section ${sectionId} already loaded`)
      return
    }
    
    // Find section metadata
    const section = shopMetadata.sections.find(s => s.section_id === sectionId)
    if (!section) {
      console.error(`❌ [loadSection] Section ${sectionId} not found in metadata`)
      return
    }
    
    console.log(`📂 [loadSection] Loading ${section.name}: ${section.frame_count} frames from ${section.paths.cameras}`)
    
    // Start loading with progress tracking
    set({ isLoading: true, loadingProgress: 0 })
    
    try {
      // Load section data in parallel (cameras, points3d, offset_map)
      set({ loadingProgress: 10 })
      
      const [camerasResponse, pointsResponse, offsetMapResponse] = await Promise.all([
        authFetch(section.paths.cameras),
        authFetch(section.paths.points3d),
        authFetch(section.paths.offset_map)
      ])
      
      set({ loadingProgress: 40 })
      
      if (!camerasResponse.ok) throw new Error(`Failed to load cameras for section ${sectionId}`)
      if (!pointsResponse.ok) throw new Error(`Failed to load points3d for section ${sectionId}`)
      if (!offsetMapResponse.ok) throw new Error(`Failed to load offset_map for section ${sectionId}`)
      
      set({ loadingProgress: 60 })
      
      const camerasData = await camerasResponse.json()
      const pointsData = await pointsResponse.json()
      // Note: offsetMapData loaded but not yet merged (TODO: Phase 2.2)
      await offsetMapResponse.json() as OffsetMap
      
      set({ loadingProgress: 90 })
      
      console.log(`✅ [loadSection] Section ${sectionId} loaded: ${camerasData.cameras?.length || 0} cameras, ${pointsData.points?.length || 0} points`)
      
      // TODO: Merge section data into main state
      // For now, we'll just track that it's loaded
      // In Phase 2.2, we'll need to merge cameras/points/offsetMap arrays
      
      // Mark section as loaded
      const newLoadedSectionIds = new Set(loadedSectionIds)
      newLoadedSectionIds.add(sectionId)
      set({ loadedSectionIds: newLoadedSectionIds, loadingProgress: 100 })
      
      console.log(`✅ [loadSection] Section ${sectionId} marked as loaded (${newLoadedSectionIds.size}/${shopMetadata.total_sections} loaded)`)
      
    } catch (error) {
      console.error(`❌ [loadSection] Error loading section ${sectionId}:`, error)
      throw error
    } finally {
      // Always reset loading state when done
      set({ isLoading: false })
    }
  },
  
  // ===== STAGE 2: Background Loading (runs in background after Stage 1 completes) =====
  // This function loads all the heavy data: cameras, points, offset_map, and builds structures
  // It does NOT block pan - Stage 1 has already made pan ready
  loadStage2Data: async (dataset: Dataset, manifestData: any, isMultiSection: boolean, shopMetadata: ShopMetadata | null) => {
    console.log(`🔄 [loadStage2Data] STAGE 2 STARTING - Background loading (non-blocking)`)
    
    try {
      set({ loadingProgress: 25, loadingStage: 'Loading cameras...' })
      
      // Load cameras.json
      let camerasResponse = await authFetch(dataset.paths.cameras)
      let camerasPath = dataset.paths.cameras
      
      // Backward compatibility: If new path fails and this is a multi-section shop, try old root path
      if (!camerasResponse.ok && isMultiSection && dataset.paths.cameras.includes('/section_0/')) {
        const oldPath = dataset.paths.cameras.replace('/section_0/', '/')
        console.log(`⚠️  [loadStage2Data] New path failed, trying old root path: ${oldPath}`)
        const oldResponse = await authFetch(oldPath)
        if (oldResponse.ok) {
          camerasResponse = oldResponse
          camerasPath = oldPath
        }
      }
      
      if (!camerasResponse.ok) throw new Error(`Failed to load cameras from ${camerasPath}`)
      const camerasData = await camerasResponse.json()
      console.log(`📷 [loadStage2Data] Cameras loaded: ${camerasData.cameras?.length || 0} cameras`)
      
      set({ loadingProgress: 45, loadingStage: 'Loading point cloud...' })
      
      // Try simplified points first, fallback to full
      let pointsData
      let pointsPath = dataset.paths.points3d_simplified
      let simplifiedResponse = await fetch(toBlobUrl(pointsPath))
      let isJSON = simplifiedResponse.ok && simplifiedResponse.headers.get('content-type')?.includes('application/json')
      
      if (!isJSON && pointsPath.includes('/section_0/')) {
        const oldPath = pointsPath.replace('/section_0/', '/')
        simplifiedResponse = await fetch(toBlobUrl(oldPath))
        isJSON = simplifiedResponse.ok && simplifiedResponse.headers.get('content-type')?.includes('application/json')
        if (isJSON) pointsPath = oldPath
      }
      
      if (isJSON) {
        pointsData = await simplifiedResponse.json()
      } else {
        pointsPath = dataset.paths.points3d
        let fullResponse = await fetch(toBlobUrl(pointsPath))
        if (!fullResponse.ok && pointsPath.includes('/section_0/')) {
          const oldPath = pointsPath.replace('/section_0/', '/')
          fullResponse = await fetch(toBlobUrl(oldPath))
          if (fullResponse.ok) pointsPath = oldPath
        }
        if (!fullResponse.ok) throw new Error(`Failed to load point cloud from ${pointsPath}`)
        pointsData = await fullResponse.json()
      }
      console.log(`📍 [loadStage2Data] Points loaded: ${pointsData.points?.length || 0} points`)
      
      set({ loadingProgress: 70, loadingStage: 'Loading offset map...' })
      
      // Load offset_map (optional)
      let offsetMapData: OffsetMap | null = null
      let offsetMapLoadTime: number | null = null
      let offsetMapsData: Map<number, OffsetMap> | null = null
      
      try {
        const offsetMapStart = performance.now()
        
        if (isMultiSection && shopMetadata) {
          // Multi-section offset maps
          console.log(`[loadStage2Data] 🗺️  Loading ${shopMetadata.sections.length} section offset_maps...`)
          
          const sectionOffsetMaps = await Promise.all(
            shopMetadata.sections.map(async (section) => {
              try {
                const response = await authFetch(section.paths.offset_map)
                if (!response.ok) return null
                const offsetMap = await response.json() as OffsetMap
                
                if (offsetMap.all_pairs) {
                  for (const key in offsetMap.all_pairs) {
                    const offset = offsetMap.all_pairs[key]
                    if (!offset.distance_3d) {
                      offset.distance_3d = calculateDistance3D(offset)
                    }
                  }
                }
                
                return { sectionId: section.section_id, offsetMap }
              } catch (error) {
                console.warn(`[loadStage2Data] ⚠️  Failed to load offset_map for section ${section.section_id}:`, error)
                return null
              }
            })
          )
          
          offsetMapsData = new Map()
          sectionOffsetMaps.forEach((item) => {
            if (item) offsetMapsData!.set(item.sectionId, item.offsetMap)
          })
          
          console.log(`[loadStage2Data] ✅ Loaded ${offsetMapsData.size} section offset_maps`)
          offsetMapLoadTime = performance.now() - offsetMapStart
          
          // FALLBACK: If we didn't load all, try single merged offset_map
          if (offsetMapsData.size < shopMetadata.sections.length) {
            console.log(`[loadStage2Data] ⚠️  Falling back to single offset_map`)
            const reconstructionDir = dataset.paths.cameras.substring(0, dataset.paths.cameras.lastIndexOf('/'))
            const offsetMapPath = `${reconstructionDir}/offset_map.json`
            try {
              const offsetMapResponse = await fetch(toBlobUrl(offsetMapPath))
              if (offsetMapResponse.ok) {
                offsetMapData = await offsetMapResponse.json() as OffsetMap
                
                if (offsetMapData.all_pairs) {
                  for (const key in offsetMapData.all_pairs) {
                    const offset = offsetMapData.all_pairs[key]
                    if (!offset.distance_3d) {
                      offset.distance_3d = calculateDistance3D(offset)
                    }
                  }
                }
                console.log(`[loadStage2Data] ✅ Loaded fallback offset_map`)
              }
            } catch (e) {
              console.warn(`[loadStage2Data] ⚠️  Failed to load fallback offset_map:`, e)
            }
            offsetMapsData = null
          }
          
          // Create merged offset_map for connectivity
          const frameNumberToIndex = new Map<number, number>()
          camerasData.cameras.forEach((camera: Camera, index: number) => {
            const frameNumber = camera.frame_number || camera.id
            frameNumberToIndex.set(frameNumber, index)
          })
          
          offsetMapData = {
            all_pairs: {},
            sequential: {},
            metadata: {
              version: '1.0',
              frameCount: camerasData.cameras.length,
              sceneCenter: [0, 0, 0] as [number, number, number],
              generated: new Date().toISOString()
            }
          }
          
          shopMetadata.sections.forEach((section) => {
            const sectionOffsetMap = offsetMapsData!.get(section.section_id)
            if (!sectionOffsetMap || !offsetMapData) return
            
            if (sectionOffsetMap.all_pairs && offsetMapData.all_pairs) {
              for (const key in sectionOffsetMap.all_pairs) {
                const [localFrom, localTo] = key.split('_').map(Number)
                const videoFrameFrom = section.frame_range[0] + localFrom
                const videoFrameTo = section.frame_range[0] + localTo
                const globalFrom = frameNumberToIndex.get(videoFrameFrom)
                const globalTo = frameNumberToIndex.get(videoFrameTo)
                
                if (globalFrom !== undefined && globalTo !== undefined) {
                  const globalKey = `${globalFrom}_${globalTo}`
                  offsetMapData.all_pairs[globalKey] = sectionOffsetMap.all_pairs[key]
                }
              }
            }
            
            if (sectionOffsetMap.sequential && offsetMapData.sequential) {
              for (const key in sectionOffsetMap.sequential) {
                const [localFrom, localTo] = key.split('_').map(Number)
                const videoFrameFrom = section.frame_range[0] + localFrom
                const videoFrameTo = section.frame_range[0] + localTo
                const globalFrom = frameNumberToIndex.get(videoFrameFrom)
                const globalTo = frameNumberToIndex.get(videoFrameTo)
                
                if (globalFrom !== undefined && globalTo !== undefined) {
                  const globalKey = `${globalFrom}_${globalTo}`
                  offsetMapData.sequential[globalKey] = sectionOffsetMap.sequential[key]
                }
              }
            }
          })
          
          console.log(`[loadStage2Data] ✅ Created merged offset_map`)
          
        } else {
          // Legacy: Single offset_map
          const reconstructionDir = dataset.paths.cameras.substring(0, dataset.paths.cameras.lastIndexOf('/'))
          const offsetMapPath = `${reconstructionDir}/offset_map.json`
          const offsetMapResponse = await fetch(toBlobUrl(offsetMapPath))
          
          if (offsetMapResponse.ok) {
            offsetMapData = await offsetMapResponse.json() as OffsetMap
            
            if (offsetMapData.all_pairs) {
              let precomputedCount = 0
              for (const key in offsetMapData.all_pairs) {
                const offset = offsetMapData.all_pairs[key]
                if (!offset.distance_3d) {
                  offset.distance_3d = calculateDistance3D(offset)
                  precomputedCount++
                }
              }
              console.log(`[loadStage2Data] ✅ Pre-computed distance_3d for ${precomputedCount} offsets`)
            }
            
            offsetMapLoadTime = performance.now() - offsetMapStart
          }
        }
      } catch (error) {
        console.warn(`[loadStage2Data] ⚠️  Offset map loading failed (optional):`, error)
      }
      
      set({ loadingProgress: 80, loadingStage: 'Building spatial structures...' })
      
      // Detect pixel data format
      const hasPixelData = !!(
        camerasData.metadata?.version === '2.0' &&
        camerasData.metadata?.has_pixel_data === true &&
        camerasData.point_to_cameras
      )
      
      // Build spatial structures
      const mappings = buildPointCloudMappings(
        camerasData.cameras, 
        pointsData.points,
        camerasData.camera_to_points
      )
      
      const pointKDTree = createPointKDTree(pointsData.points)
      const cameraKDTree = createCameraKDTree(camerasData.cameras)
      console.log(`[loadStage2Data] ✅ Spatial structures built`)
      
      // Build pixel cache
      let initialPixelCache: Map<number, [number, number]> | null = null
      let initialPixelCacheStats = null
      
      if (hasPixelData && camerasData.cameras.length > 0) {
        const cache = new PixelCache()
        const success = cache.buildFromCamera(camerasData.cameras[0])
        if (success) {
          initialPixelCache = cache.getCache()
          initialPixelCacheStats = cache.getStats()
        }
      }
      
      set({ loadingProgress: 85, loadingStage: 'Finalizing structures...' })
      
      // Detect sections
      let detectedSections: Section[] = []
      
      if (isMultiSection && shopMetadata) {
        detectedSections = shopMetadata.sections.map((s) => {
          const [startIdx, endIdx] = s.frame_range
          const frames = Array.from({ length: s.frame_count }, (_, i) => startIdx + i)
          
          return {
            sectionId: s.section_id,
            startFrame: startIdx,
            endFrame: endIdx,
            frameCount: s.frame_count,
            frames
          }
        })
        console.log(`[loadStage2Data] ✅ Loaded ${detectedSections.length} sections`)
      } else if (offsetMapData && camerasData.cameras.length > 0) {
        detectedSections = detectSectionsFromOffsets(offsetMapData, camerasData.cameras.length)
        console.log(`[loadStage2Data] ✅ Detected ${detectedSections.length} sections`)
      }
      
      set({ loadingProgress: 90, loadingStage: 'Building connectivity...' })
      
      // Build connectivity graph
      let connectivityGraphData: Map<number, Set<number>> | null = null
      let extendedConnectivityGraphData: Map<number, Set<number>> | null = null
      
      if (manifestData) {
        // PREFER manifest connectivity
        const { buildConnectivityGraphFromManifest } = await import('../utils/offsetMapUtils')
        connectivityGraphData = buildConnectivityGraphFromManifest(manifestData)
        console.log(`[loadStage2Data] ✅ Connectivity graph from manifest`)
      } else if (offsetMapData && camerasData.cameras.length > 0) {
        // Fallback: from offset_map
        connectivityGraphData = buildConnectivityGraph(offsetMapData, camerasData.cameras.length)
        console.log(`[loadStage2Data] ✅ Connectivity graph from offset_map`)
      }
      
      if (connectivityGraphData) {
        const { buildExtendedConnectivityGraph } = await import('../utils/framePathFinding')
        extendedConnectivityGraphData = buildExtendedConnectivityGraph(
          connectivityGraphData,
          offsetMapData,
          camerasData.cameras.length
        )
        console.log(`[loadStage2Data] ✅ Extended connectivity graph built`)
      }
      
      set({ loadingProgress: 95, loadingStage: 'Completing load...' })
      
      // Determine first valid frame
      const minFrameIndex = isMultiSection && detectedSections.length > 0 
        ? Math.min(...detectedSections.map(section => section.startFrame))
        : 0
      
      // Calculate anchored positions
      let initialAnchoredPositions: Array<{ frameIndex: number; worldX: number; worldY: number }> = []
      const state = get()
      if (offsetMapData && camerasData.cameras.length > 0 && state.frameWorldPositions.length > 0) {
        initialAnchoredPositions = calculateAnchoredFramePositions(
          minFrameIndex,
          offsetMapData,
          camerasData.cameras.length,
          state.frameWorldPositions
        )
      }
      
      // Build candidate list
      let initialCandidates = [minFrameIndex]
      if (connectivityGraphData) {
        const connectedSet = connectivityGraphData.get(minFrameIndex)
        if (connectedSet) {
          const connectedArray = Array.from(connectedSet).sort((a, b) => a - b)
          initialCandidates = [minFrameIndex, ...connectedArray]
        }
      }
      
      // Commit Stage 2 to state - TAP IS NOW READY
      set({
        cameras: camerasData.cameras,
        points3D: pointsData.points,
        pointCloudMappings: mappings,
        pointKDTree,
        cameraKDTree,
        hasPixelData,
        pixelCache: initialPixelCache,
        pixelCacheStats: initialPixelCacheStats,
        offsetMap: offsetMapData,
        hasOffsetMap: !!offsetMapData || !!offsetMapsData,
        offsetMapLoadTime: offsetMapLoadTime,
        offsetMaps: offsetMapsData,
        connectivityGraph: connectivityGraphData,
        extendedConnectivityGraph: extendedConnectivityGraphData,
        anchoredFramePositions: initialAnchoredPositions,
        sections: detectedSections,
        currentSectionId: 0,
        currentSectionFramesPath: state.dataset?.paths.frames || '',
        candidateFrameIndices: initialCandidates,
        isLoadingData: false,
        canUseTapIndicator: true,  // ✅ TAP NOW WORKS!
        loadingProgress: 100,
        loadingStage: 'Complete!',
        dataError: null
      })
      
      console.log(`✅ [loadStage2Data] STAGE 2 COMPLETE - TAP IS NOW ENABLED`)
      
      // Initialize Frame Navigation Strategy
      if (camerasData.cameras.length > 0) {
        const freshState = useAppStore.getState()
        const frameWidth = camerasData.cameras[0].width || 1920
        const frameHeight = camerasData.cameras[0].height || 1080
        freshState.initializeFrameNavigationStrategy(frameWidth, frameHeight)
      }
      
      // Auto-load section if multi-section
      if (isMultiSection && shopMetadata) {
        const sectionWithMaxCameras = shopMetadata.sections.reduce((max, current) => 
          current.camera_count > max.camera_count ? current : max
        , shopMetadata.sections[0])
        
        console.log(`🔄 [loadStage2Data] Auto-loading section ${sectionWithMaxCameras.section_id}`)
        
        get().loadSectionAsIndependentShop(state.currentDatasetId!, sectionWithMaxCameras.section_id).then(() => {
          get().playAllFramesInSection()
        }).catch((error) => {
          console.error(`⚠️ [loadStage2Data] Auto-load failed:`, error)
        })
      }
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error(`❌ [loadStage2Data] STAGE 2 FAILED:`, error)
      set({ dataError: errorMessage, isLoadingData: false, canUseTapIndicator: false })
    }
  },
  
  loadDataset: async (datasetId, options) => {
    console.log(`🔗 [loadDataset] ===== STAGE 1: PAN-READY (Manifest Only) =====`)
    const { availableDatasets } = get()
    // Try exact match first, then fall back to matching by shopId field
    let dataset = availableDatasets.find(d => d.id === datasetId)
    if (!dataset) {
      // Find all datasets for this shop, pick the one with highest camera_count (best reconstruction)
      const shopMatches = availableDatasets.filter(d => d.shopId === datasetId)
      if (shopMatches.length > 0) {
        dataset = shopMatches.sort((a, b) => (b.camera_count ?? 0) - (a.camera_count ?? 0))[0]
        console.log(`🔗 [loadDataset] Resolved shopId ${datasetId} → best dataset ${dataset.id} (${shopMatches.length} total)`)
        datasetId = dataset.id
      }
    }
    
    // Compute shopDatasets: all datasets for this shop, deduped by sectionId (latest version wins)
    const shopId = dataset?.shopId || datasetId
    const allShopDatasets = availableDatasets.filter(d => d.shopId === shopId || d.id === shopId)
    const sectionMap = new Map<string, Dataset>()
    for (const ds of allShopDatasets) {
      const secId = ds.sectionId || ds.id // fallback to dataset id for legacy datasets
      const existing = sectionMap.get(secId)
      if (!existing || (ds.version ?? 0) > (existing.version ?? 0) || 
          (!ds.version && !existing.version && (ds.camera_count ?? 0) > (existing.camera_count ?? 0))) {
        sectionMap.set(secId, ds)
      }
    }
    const shopDatasets = Array.from(sectionMap.values())
    const activeUserSectionId = dataset?.sectionId || null
    console.log(`🏪 [loadDataset] Shop has ${shopDatasets.length} section dataset(s), active: ${activeUserSectionId}`)
    
    if (!dataset) {
      console.error(`❌ [loadDataset] Dataset not found: ${datasetId}`)
      set({ dataError: `Dataset ${datasetId} not found` })
      return
    }
    
    console.log(`✅ [loadDataset] Found dataset:`, dataset.name)
    
    try {
      // ===== STAGE 1: Load manifest only (quick, ~150ms) =====
      set({ isLoadingData: true, dataError: null, currentDatasetId: datasetId, currentShopId: datasetId, loadingProgress: 0, loadingStage: 'Starting...', loadedSectionIds: new Set(), shopDatasets, activeUserSectionId })
      
      // Check for shop_metadata
      set({ loadingProgress: 5, loadingStage: 'Checking shop metadata...' })
      const shopMetadata = await get().loadShopMetadata(dataset)
      const isMultiSection = !!shopMetadata
      
      if (isMultiSection) {
        console.log(`🏪 [loadDataset] Multi-section shop detected: ${shopMetadata!.total_sections} section(s)`)
        
        // Find best section ONCE based on score and frame count
        // NOTE: Use spread to avoid mutating shopMetadata.sections (sort() mutates in-place)
        const bestSection = [...shopMetadata!.sections].sort((a: any, b: any) => 
          (b.score ?? 0) - (a.score ?? 0) || (b.frame_count ?? 0) - (a.frame_count ?? 0)
        )[0]
        const bestSectionId = bestSection?.section_id ?? 0
        
        // Use target section from URL params if provided, otherwise use best section
        const targetSectionId = options?.section !== undefined ? options.section : bestSectionId
        
        if (options?.section !== undefined) {
          console.log(`🔗 [loadDataset] Loading URL target section: ${targetSectionId}`)
        } else {
          console.log(`🏆 [loadDataset] Loading best section: ${bestSection?.name} (ID: ${bestSectionId}, score: ${bestSection?.score})`)
        }
        
        // Populate sections array from shopMetadata for section selector/drawer
        const sections = shopMetadata!.sections.map((s) => {
          const [startIdx, endIdx] = s.frame_range
          const frames = Array.from({ length: s.frame_count }, (_, i) => startIdx + i)
          return {
            sectionId: s.section_id,
            startFrame: startIdx,
            endFrame: endIdx,
            frameCount: s.frame_count,
            frames
          }
        })
        
        set({ shopMetadata, isMultiSection, currentSectionId: targetSectionId, sections })
        
        // Load full data for target section via unified switchToSection
        console.log(`\n📥 [loadDataset] Loading section ${targetSectionId} data via switchToSection...`)
        set({ loadingProgress: 50, loadingStage: 'Loading section data...' })
        await get().switchToSection(targetSectionId, { skipAnimation: options?.skipAnimation })
        set({ isLoadingData: false, loadingProgress: 100, loadingStage: '' })
        
        // Navigate to target frame if specified in URL
        if (options?.frame !== undefined) {
          console.log(`🔗 [loadDataset] Navigating to URL target frame: ${options.frame}`)
          
          // Cancel any ongoing animation before navigating to target frame
          const state = get()
          if (state.isFrameSequenceAnimating) {
            console.log(`⏹️ [loadDataset] Canceling ongoing animation before URL navigation`)
            state.cancelFrameSequenceAnimation()
          }
          
          // Use longer delay to ensure store state is fully updated after switchToSection
          const frameNavTimeout = setTimeout(() => {
            const state = get()
            if (options.frame! >= 0 && options.frame! < state.cameras.length) {
              state.setManuallySelectedFrame(options.frame!)
              state.centerViewportOnFrame(options.frame!)
              console.log(`✅ [loadDataset] Navigated to frame ${options.frame}`)
              
              // Clear URL params after successful navigation to prevent issues on refresh
              // EXCEPT: Don't clear section/frame if this is a shared item link (has x, y, r parameters)
              try {
                const urlParams = new URLSearchParams(window.location.search)
                const isSharedItemLink = urlParams.has('x') && urlParams.has('y') && urlParams.has('r')
                
                if (!isSharedItemLink) {
                  urlParams.delete('section')
                  urlParams.delete('frame')
                  const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`
                  window.history.replaceState({}, '', newUrl)
                  console.log(`🔗 [loadDataset] Cleared URL navigation params`)
                } else {
                  console.log(`🔗 [loadDataset] Keeping URL params for shared item link`)
                }
              } catch (err) {
                console.warn(`⚠️ [loadDataset] Failed to clear URL params:`, err)
              }
            } else {
              console.warn(`⚠️ [loadDataset] Frame ${options.frame} out of bounds (0-${state.cameras.length - 1})`)
            }
          }, 200)
          
          // Store timeout for potential cleanup (though loadDataset doesn't expose cancellation)
          // This ensures proper cleanup if implementation changes
          set({ _urlNavigationTimeout: frameNavTimeout } as any)
        }
        
        return
      } else {
        console.log(`🏪 [loadDataset] Legacy shop`)
        set({ shopMetadata: null, isMultiSection: false })
      }
      
      // Load manifest (if available)
      set({ loadingProgress: 10, loadingStage: 'Loading manifest...' })
      let manifestData: any = null
      try {
        const reconstructionDir = dataset.paths.cameras.substring(0, dataset.paths.cameras.lastIndexOf('/'))
        const manifestPath = `${reconstructionDir}/manifest.json`
        manifestData = await loadManifestWithFallback(manifestPath)
        if (manifestData) {
          console.log(`✅ [loadDataset] Manifest loaded: ${manifestData.frames?.length || 0} frames`)
        } else {
          console.warn(`⚠️  [loadDataset] No manifest available`)
        }
      } catch (error) {
        console.warn(`⚠️  [loadDataset] Manifest load failed (optional):`, error)
      }
      
      // Calculate world positions from manifest
      set({ loadingProgress: 15, loadingStage: 'Calculating positions...' })
      let worldPositions: Array<{ frameIndex: number; worldX: number; worldY: number }> = []
      
      if (manifestData) {
        const { calculateFrameWorldPositionsFromManifest } = await import('../utils/worldSpace')
        const frameWidth = 1920 // Will be refined in Stage 2
        const frameHeight = 1080
        worldPositions = calculateFrameWorldPositionsFromManifest(manifestData, frameWidth, frameHeight)
        console.log(`✅ [loadDataset] World positions calculated: ${worldPositions.length} frames`)
      }
      
      // ===== STEP 1: Generate proxy cameras from manifest =====
      set({ loadingProgress: 18, loadingStage: 'Generating proxy cameras...' })
      let proxyCameras: Camera[] = []
      if (manifestData && manifestData.frames) {
        proxyCameras = manifestData.frames.map((frame: any, index: number) => {
          // Extract camera model data - handle both array and object formats
          let cameraModel: any = null
          
          if (Array.isArray(manifestData.camera_models)) {
            const cameraModelId = frame.camera_model_id || 0
            cameraModel = manifestData.camera_models.find((cm: any) => cm.model_id === cameraModelId) || manifestData.camera_models[0]
          } else if (typeof manifestData.camera_models === 'object' && manifestData.camera_models !== null) {
            // If camera_models is an object, try to get it by ID
            const cameraModelId = frame.camera_model_id || 0
            cameraModel = manifestData.camera_models[cameraModelId]
          }
          
          return {
            id: index,
            intrinsics: {
              width: cameraModel?.width || 1920,
              height: cameraModel?.height || 1080,
              fx: cameraModel?.params?.[0] || 1000,
              fy: cameraModel?.params?.[1] || 1000,
              cx: cameraModel?.params?.[2] || 960,
              cy: cameraModel?.params?.[3] || 540,
              k1: cameraModel?.params?.[4] || 0,
              k2: cameraModel?.params?.[5] || 0,
            },
            // Dummy position - will be improved later
            position: [0, 0, 0] as [number, number, number],
            // Extract frame file for rendering
            frame_file: frame.frame_file || `frame_${String(index).padStart(4, '0')}.jpg`,
            // Extract connected frames from manifest
            observed_points: [],
            // Store rotation/translation as-is (not used in proxy mode)
            rotation: frame.rotation || [[1, 0, 0], [0, 1, 0], [0, 0, 1]],
            translation: frame.translation || [0, 0, 0],
          } as unknown as Camera
        })
        console.log(`✅ [loadDataset] STEP 1: Generated ${proxyCameras.length} proxy cameras from manifest`)
      }
      
      // ===== STEP 2: Build connectivity graph from manifest offsets =====
      set({ loadingProgress: 19, loadingStage: 'Building connectivity graph...' })
      let connectivityGraphData: Map<number, Set<number>> = new Map()
      if (manifestData && manifestData.frames) {
        connectivityGraphData = new Map()
        manifestData.frames.forEach((frame: any, frameIndex: number) => {
          const neighbors = new Set<number>()
          
          // Extract neighbors from frame.offsets or frame.neighbors array
          if (frame.offsets && Array.isArray(frame.offsets)) {
            // offsets format: array of [neighborIndex, offsetX, offsetY] or similar
            frame.offsets.forEach((offset: any) => {
              const neighborIndex = typeof offset === 'object' ? offset[0] : offset
              if (typeof neighborIndex === 'number') {
                neighbors.add(neighborIndex)
              }
            })
          } else if (frame.neighbors && Array.isArray(frame.neighbors)) {
            // neighbors format: simple array of neighbor indices
            frame.neighbors.forEach((neighborIndex: number) => {
              neighbors.add(neighborIndex)
            })
          }
          
          connectivityGraphData.set(frameIndex, neighbors)
        })
        console.log(`✅ [loadDataset] STEP 2: Built connectivity graph with ${connectivityGraphData.size} frames`)
      }
      
      // ===== STEP 3: Detect sections from shop metadata =====
      set({ loadingProgress: 19, loadingStage: 'Detecting sections...' })
      let detectedSections: Section[] = []
      if (isMultiSection && shopMetadata) {
        detectedSections = shopMetadata.sections.map((s) => {
          const [startIdx, endIdx] = s.frame_range
          const frames = Array.from({ length: s.frame_count }, (_, i) => startIdx + i)
          
          return {
            sectionId: s.section_id,
            startFrame: startIdx,
            endFrame: endIdx,
            frameCount: s.frame_count,
            frames
          }
        })
        console.log(`✅ [loadDataset] STEP 3: Detected ${detectedSections.length} sections from metadata`)
      }
      
      // ===== COMMIT STAGE 1: PAN IS NOW READY ===== 
      set({
        manifest: manifestData,
        hasManifest: !!manifestData,
        frameWorldPositions: worldPositions,
        cameras: proxyCameras,  // ✅ STEP 1: Add proxy cameras to state
        connectivityGraph: connectivityGraphData,  // ✅ STEP 2: Add connectivity graph to state
        points3D: [],  // Empty for manifest-only mode (graceful degrade)
        sections: detectedSections,  // ✅ STEP 3: Add detected sections
        currentSectionId: detectedSections.length > 0 ? detectedSections[0].sectionId : 0,
        isDrawerOpen: false,  // Sections available but drawer starts closed

        currentCameraIndex: 0,
        viewportWorldPosition: { x: 0, y: 0 },
        isLoadingData: true,  // Still loading, but pan is ready
        canUseTapIndicator: true,  // 🧪 TEMP: Testing sections drawer visibility
        loadingProgress: 20,
        loadingStage: 'Pan ready! Loading 3D data...',
        currentDatasetId: datasetId,
        currentShopId: datasetId,
        currentSectionFramesPath: dataset.paths.frames  // ✅ Set frames path so images can load
      })
      
      console.log(`🎯 [loadDataset] STAGE 1 COMPLETE - PAN IS READY`)
      
      // ===== STAGE 2: Load full 3D data in background (non-blocking) =====
      setTimeout(() => {
        get().loadStage2Data(dataset!, manifestData, isMultiSection, shopMetadata)
          .catch(error => {
            console.error(`❌ [loadDataset] STAGE 2 FAILED:`, error)
          })
      }, 100)
      
      // Return immediately - pan is ready!
      console.log(`✅ [loadDataset] RETURNING - Pan is ready, Stage 2 loading in background`)
      
    } catch (error) {
      // Stage 1 failed
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error(`❌ [loadDataset] STAGE 1 FAILED:`, error)
      set({ dataError: errorMessage, isLoadingData: false, canUseTapIndicator: false, loadingProgress: 0, loadingStage: '' })
    }
  },
  
  setCurrentShop: (shopId) => {
    console.log('🛍️ [Store] setCurrentShop called:', shopId)
    // When switching shops, reset ALL shop-specific state to prevent data mixing
    set({ 
      currentShopId: shopId,
      currentDatasetId: null,  // Force loadDataset() to run on any shop navigation
      // Reset camera/frame navigation
      currentCameraIndex: 0,
      nextCameraIndex: null,
      // Reset viewport position to origin
      panOffset: { x: 0, y: 0 },
      zoomScale: 1.0,
      isPanning: false,
      isZooming: false,
      // Reset visible cameras
      visibleCameraIndices: [0],
      visibleCameraOffsets: new Map([[0, { x: 0, y: 0 }]]),
      // Reset world space viewport to frame 0
      viewportWorldPosition: { x: 0, y: 0 },
      // Reset frame selection
      manuallySelectedFrameIndex: 0,
      lastManualSelectionTime: 0,
      currentFrameSelection: null,
      // Reset UI state
      isDrawerOpen: false,
      // Force viewport re-render
      viewportVersion: 0,
      // ✅ Reset multi-section/shop-specific data to prevent mixing between shops
      shopMetadata: null,
      sections: [],
      currentSectionId: 0,
      isMultiSection: false,
      currentSectionFramesPath: null,
    })
  },
  
  setLastSelectedShop: (shopId) => set({ lastSelectedShop: shopId }),
  
  setSelectedShop: (shopId) => set({ currentShopId: shopId }),
  
  setIsLoadingDatasets: (loading) => set({ isLoadingDatasets: loading }),

  setAvailableShops: (shops) => set({ availableShops: shops }),

  setCOLMAPData:(cameras, points) => {
    // Initialize visible frames for first camera
    const { offsetMap, connectivityGraph } = get()
    let visibleIndices = [0]
    let visibleOffsets = new Map<number, { x: number; y: number }>([[0, { x: 0, y: 0 }]])
    
    // If offset map available and cameras exist, calculate ±2 frames
    if (offsetMap && cameras.length > 0) {
      const result = calculateVisibleFramesSimple(0, 2, cameras.length, offsetMap)
      if (result.indices.length > 0) {
        visibleIndices = result.indices
        visibleOffsets = result.offsets
      }
    }
    
    // Phase 2.7: Calculate world space positions for all frames
    let worldPositions: Array<{ frameIndex: number; worldX: number; worldY: number }> = []
    console.log('🌍 [setCOLMAPData] About to calculate world positions:', {
      hasOffsetMap: !!offsetMap,
      offsetMapKeys: offsetMap ? Object.keys(offsetMap.sequential || {}).length : 0,
      camerasLength: cameras.length
    })
    
    if (offsetMap && cameras.length > 0) {
      console.log('🌍 [setCOLMAPData] Calculating world space positions...')
      const frameWidth = cameras[0].intrinsics?.width || 1920
      const frameHeight = cameras[0].intrinsics?.height || 1080
      worldPositions = calculateFrameWorldPositions(offsetMap, cameras.length, undefined, frameWidth, frameHeight)
      console.log('✅ [setCOLMAPData] World positions calculated:', worldPositions.length, 'frames')
      console.log('✅ [setCOLMAPData] First position:', worldPositions[0])
      console.log('✅ [setCOLMAPData] Last position:', worldPositions[worldPositions.length - 1])
    } else {
      console.error('❌ [setCOLMAPData] Cannot calculate world positions!', {
        hasOffsetMap: !!offsetMap,
        camerasLength: cameras.length
      })
    }
    
    // Calculate initial anchored positions (frame 0 as anchor)
    let initialAnchoredPositions: Array<{ frameIndex: number; worldX: number; worldY: number }> = []
    if (offsetMap && cameras.length > 0) {
      initialAnchoredPositions = calculateAnchoredFramePositions(0, offsetMap, cameras.length, worldPositions)
    }
    
    // Build candidate list for first frame: frame 0 + connected neighbors
    let initialCandidates = [0]
    if (connectivityGraph) {
      const connectedSet = connectivityGraph.get(0)
      if (connectedSet) {
        const connectedArray = Array.from(connectedSet).sort((a, b) => a - b)
        initialCandidates = [0, ...connectedArray]
      }
    }
    
    set({ 
      cameras, 
      missingFrames: new Set<string>(),
      points3D: points,
      currentCameraIndex: 0,
      visibleCameraIndices: visibleIndices,
      visibleCameraOffsets: visibleOffsets,
      frameWorldPositions: worldPositions,
      anchoredFramePositions: initialAnchoredPositions,
      viewportWorldPosition: worldPositions[0] ? { x: worldPositions[0].worldX, y: worldPositions[0].worldY } : { x: 0, y: 0 },
      isLoadingData: false,
      dataError: null,
      candidateFrameIndices: initialCandidates, // Frame 0 + connected neighbors
      manuallySelectedFrameIndex: 0, // Select first frame on load
    })
    
    console.log('✅ [setCOLMAPData] State updated with frameWorldPositions:', worldPositions.length, 'items')
    console.log('✅ [setCOLMAPData] Viewport initialized at:', worldPositions[0] ? `(${worldPositions[0].worldX.toFixed(2)}, ${worldPositions[0].worldY.toFixed(2)})` : '(0, 0)')
  },
  
  markFrameMissing: (frameFile) => {
    const prev = get().missingFrames
    if (prev.has(frameFile)) return // already tracked
    const next = new Set(prev)
    next.add(frameFile)
    set({ missingFrames: next })
  },

  setLoadingData: (loading) => set({ isLoadingData: loading }),

  loadFromDirectUrl: async (camerasUrl, base, sas) => {
    set({ isLoadingData: true, canUseTapIndicator: false })
    try {
      const appendSas = (url: string) => sas ? `${url}${url.includes('?') ? '&' : '?'}${sas}` : url
      const [camRes, ptsRes] = await Promise.all([
        fetch(camerasUrl),
        fetch(appendSas(`${base.endsWith('/') ? base : base + '/'}points3d.json`)),
      ])
      const camData = camRes.ok ? await camRes.json() : null
      const ptsData = ptsRes.ok ? await ptsRes.json() : null
      if (camData?.cameras) {
        set({
          cameras: camData.cameras,
          points3D: ptsData?.points ?? [],
          canUseTapIndicator: true,
          isLoadingData: false,
        })
      } else {
        set({ isLoadingData: false })
      }
    } catch {
      set({ isLoadingData: false })
    }
  },
  
  setCanUseTapIndicator: (canUse) => set({ canUseTapIndicator: canUse }),
  
  setLoadingProgress: (progress, stage) => set({ loadingProgress: Math.min(100, Math.max(0, progress)), loadingStage: stage }),
  
  setDataError: (error) => set({ dataError: error, isLoadingData: false }),
  
  setWarmStartFrame: (frame) => set({ warmStartFrame: frame }),

  setMinimapNavData: (data) => {
    if (!data) {
      set({ minimapNavData: null, corridorDirectionByFrame: null })
      return
    }
    // Build corridor direction lookup: frame_number → direction (radians)
    const corridorMap = new Map<number, number>()
    if (data.frame_numbers && data.corridor_directions) {
      for (let i = 0; i < data.frame_numbers.length; i++) {
        if (i < data.corridor_directions.length) {
          corridorMap.set(data.frame_numbers[i], data.corridor_directions[i])
        }
      }
    }
    set({
      minimapNavData: data,
      corridorDirectionByFrame: corridorMap.size > 0 ? corridorMap : null,
    })
    console.log(`🗺️ [Store] Minimap nav data set: ${data.frame_numbers?.length} frames, ${corridorMap.size} corridor directions`)
  },

  setTapMinimapData: (point3D, projectionCameraIndices) => {
    if (!point3D || !projectionCameraIndices) {
      set({ tapPoint3D: null, tapDiscoveryFrameNumbers: null })
      return
    }
    // Convert camera array indices → frame_numbers for minimap lookup
    const cameras = get().cameras
    const frameNumbers = new Set<number>()
    for (const idx of projectionCameraIndices) {
      const cam = cameras[idx]
      if (cam) {
        frameNumbers.add(cam.frame_number ?? cam.id)
      }
    }
    set({
      tapPoint3D: point3D,
      tapDiscoveryFrameNumbers: frameNumbers.size > 0 ? frameNumbers : null,
    })
  },

  setPanModeEnabled: (enabled) => {
    set({ panModeEnabled: enabled })
    console.log(`🖐️ [Store] Pan mode ${enabled ? 'enabled' : 'disabled'}`)
  },

  setExploreMode: (enabled) => {
    set({ exploreMode: enabled })
    console.log(`✋ [Store] Explore mode ${enabled ? 'enabled' : 'disabled'}`)
  },
  
  navigateToCamera: (index) => {
    const { cameras, currentCameraIndex, hasPixelData, settings, pixelCache, lastCachedCameraId, offsetMap } = get()
    if (index >= 0 && index < cameras.length) {
      // Rebuild pixel cache for new camera
      const { cache, stats, cameraId } = rebuildPixelCache(
        cameras[index], 
        hasPixelData, 
        settings.raycastMode,
        pixelCache,
        lastCachedCameraId
      )
      
      // Calculate visible frames around new camera position
      const visibleResult = calculateVisibleFramesSimple(index, 2, cameras.length, offsetMap)
      
      set({ 
        currentCameraIndex: index,
        visibleCameraIndices: visibleResult.indices,
        visibleCameraOffsets: visibleResult.offsets,
        pixelCache: cache,
        pixelCacheStats: stats || get().pixelCacheStats, // Keep old stats if no rebuild
        lastCachedCameraId: cameraId
      })
    } else {
      console.log('⚠️ Store: navigateToCamera blocked - invalid index', { index, total: cameras.length })
    }
  },
  
  navigateNext: () => {
    const { cameras, currentCameraIndex, missingFrames, hasPixelData, settings, pixelCache, lastCachedCameraId, offsetMap } = get()
    // Find the next non-missing camera index
    let newIndex = currentCameraIndex + 1
    while (newIndex < cameras.length && missingFrames.has(cameras[newIndex]?.frame_file)) {
      newIndex++
    }
    if (newIndex < cameras.length) {
      console.log('🔄 Store: navigateNext', {
        from: currentCameraIndex,
        to: newIndex,
        camera: cameras[newIndex]?.frame_file
      })
      
      // Phase 2: Rebuild pixel cache for new camera
      const { cache, stats, cameraId } = rebuildPixelCache(
        cameras[newIndex], 
        hasPixelData, 
        settings.raycastMode,
        pixelCache,
        lastCachedCameraId
      )
      
      // Calculate visible frames around new camera position
      const visibleResult = calculateVisibleFramesSimple(newIndex, 2, cameras.length, offsetMap)
      
      set({ 
        currentCameraIndex: newIndex,
        visibleCameraIndices: visibleResult.indices,
        visibleCameraOffsets: visibleResult.offsets,
        pixelCache: cache,
        pixelCacheStats: stats || get().pixelCacheStats,
        lastCachedCameraId: cameraId
      })
    } else {
      console.log('⚠️ Store: navigateNext blocked - already at last camera')
    }
  },
  
  navigatePrevious: () => {
    const { cameras, currentCameraIndex, missingFrames, hasPixelData, settings, pixelCache, lastCachedCameraId, offsetMap } = get()
    // Find the previous non-missing camera index
    let newIndex = currentCameraIndex - 1
    while (newIndex >= 0 && missingFrames.has(cameras[newIndex]?.frame_file)) {
      newIndex--
    }
    if (newIndex >= 0) {
      console.log('🔄 Store: navigatePrevious', {
        from: currentCameraIndex,
        to: newIndex,
        camera: cameras[newIndex]?.frame_file
      })
      
      // Phase 2: Rebuild pixel cache for new camera
      const { cache, stats, cameraId } = rebuildPixelCache(
        cameras[newIndex], 
        hasPixelData, 
        settings.raycastMode,
        pixelCache,
        lastCachedCameraId
      )
      
      // Calculate visible frames around new camera position
      const visibleResult = calculateVisibleFramesSimple(newIndex, 2, cameras.length, offsetMap)
      
      set({ 
        currentCameraIndex: newIndex,
        visibleCameraIndices: visibleResult.indices,
        visibleCameraOffsets: visibleResult.offsets,
        pixelCache: cache,
        pixelCacheStats: stats || get().pixelCacheStats,
        lastCachedCameraId: cameraId
      })
    } else {
      console.log('⚠️ Store: navigatePrevious blocked - already at first camera')
    }
  },
  
  setTransitioning: (transitioning) => set({ isTransitioning: transitioning }),
  
  toggleMenu: () => set((state) => ({ menuOpen: !state.menuOpen })),
  
  setMenuOpen: (open) => set({ menuOpen: open }),
  
  toggleDebugOverlay: () => set((state) => ({ 
    showDebugOverlay: !state.showDebugOverlay 
  })),

  toggleMeshViewer: () => set((state) => ({
    showMeshViewer: !state.showMeshViewer
  })),

  setCurrentMeshSection: (section: number) => set({ currentMeshSection: section }),
  
  // Controls visibility - updates BOTH controlsVisible AND showNavigationIndicators together
  // This ensures production dots appear/disappear with mobile controls on tap-to-toggle
  setControlsVisible: (visible: boolean) => {
    set({ 
      controlsVisible: visible,
      showNavigationIndicators: visible 
    })
  },
  
  // Navigation indicators - ONLY affects production-style navigation dots in CameraTrajectoryOverlay
  setShowNavigationIndicators: (visible: boolean) => set({ showNavigationIndicators: visible }),
  
  // Show/hide controls - updates BOTH controlsVisible AND showNavigationIndicators together
  // Also shows trajectory overlay so camera path is visible when controls are shown
  showControls: () => set({ 
    controlsVisible: true, 
    showNavigationIndicators: true
  }),
  
  hideControls: () => set({ controlsVisible: false, showNavigationIndicators: false }),
  
  setIs3DMode: (enabled: boolean) => {
    console.log(`🔄 [setIs3DMode] 3D mode: ${enabled ? 'ENABLED' : 'DISABLED'}`)
    set({ is3DMode: enabled })
  },
  
  updateSettings: (newSettings) => {
    const { cameras, currentCameraIndex, hasPixelData, settings, pixelCache, lastCachedCameraId } = get()
    const oldMode = settings.raycastMode
    const newMode = newSettings.raycastMode
    
    console.log('⚙️ [updateSettings] Settings change:', { oldSettings: settings, newSettings })
    
    // Check if filter settings changed (max3DDistance or maxDz)
    const filterSettingsChanged = 
      (newSettings.max3DDistance !== undefined && newSettings.max3DDistance !== settings.max3DDistance) ||
      (newSettings.maxDz !== undefined && newSettings.maxDz !== settings.maxDz)
    
    // If raycast mode changed to pixels/auto, rebuild cache immediately
    if (newMode && newMode !== oldMode && (newMode === 'pixels' || newMode === 'auto')) {
      console.log('🔄 [updateSettings] Raycast mode changed to pixels/auto, rebuilding cache...')
      
      if (cameras.length > 0 && currentCameraIndex >= 0) {
        const { cache, stats, cameraId } = rebuildPixelCache(
          cameras[currentCameraIndex], 
          hasPixelData, 
          newMode,
          pixelCache,
          lastCachedCameraId
        )
        
        set({ 
          settings: { ...settings, ...newSettings },
          pixelCache: cache,
          pixelCacheStats: stats || get().pixelCacheStats,
          lastCachedCameraId: cameraId,
          // Increment viewport version to force re-render if filters changed
          viewportVersion: filterSettingsChanged ? get().viewportVersion + 1 : get().viewportVersion
        })
        return
      }
    }
    
    // Normal settings update (no cache rebuild needed)
    set((state) => ({
      settings: { ...state.settings, ...newSettings },
      // Increment viewport version to force re-render if filters changed
      viewportVersion: filterSettingsChanged ? state.viewportVersion + 1 : state.viewportVersion
    }))
  },
  
  // Pan & Zoom actions (Phase 2.3)
  setPanOffset: (x, y) => {
    // Removed spam: console.log('👆 [setPanOffset]', { x, y })
    set({ panOffset: { x, y } })
  },
  
  setZoomScale: (scale) => {
    // Minimum 0.1 to prevent invisible/flipped image, no maximum
    const clamped = Math.max(0.1, scale)
    const state = get()
    const oldZoom = state.zoomScale
    
    // Get full stack trace to see who called this
    const stack = new Error().stack?.split('\n').slice(1, 6).map(l => l.trim()).join('\n') || 'unknown'
    
    console.log(`🔍 [setZoomScale] ZOOM CHANGED: ${oldZoom.toFixed(2)}x → ${clamped.toFixed(2)}x (requested: ${scale.toFixed(2)})`)
    console.log(`   Caller stack:`)
    console.log(stack)
    
    set({ zoomScale: clamped })
  },
  
  setPreferredDiscoveryRadiusPx: (radiusPx) => {
    console.log(`📏 [setPreferredDiscoveryRadiusPx] Saving user's preferred radius: ${radiusPx?.toFixed(1) ?? 'null'}px`)
    set({ preferredDiscoveryRadiusPx: radiusPx })
  },
  
  setIsPanning: (panning) => {
    // Removed spam: console.log('✋ [setIsPanning]', { panning })
    set({ isPanning: panning })
  },
  
  setIsZooming: (zooming) => {
    set({ isZooming: zooming })
  },
  
  // Multi-Frame Viewport (Phase 2.6)
  setVisibleCameras: (indices, offsets) => {
    console.log('🎞️ [setVisibleCameras] Setting visible frames:', {
      count: indices.length,
      indices,
      offsets: Array.from(offsets.entries()).map(([idx, off]) => `${idx}:(${off.x.toFixed(0)},${off.y.toFixed(0)})`)
    })
    set({ 
      visibleCameraIndices: indices,
      visibleCameraOffsets: offsets
    })
  },
  
  // Phase 2.7: Position-Based Navigation (World Space)
  setViewportPosition: (x, y) => {
    // console.log(`🖼️ [setViewportPosition] Setting viewport to (${x.toFixed(1)}, ${y.toFixed(1)})`)
    set((state) => ({ 
      viewportWorldPosition: { x, y },
      viewportVersion: state.viewportVersion + 1
    }))
  },
  
  panViewport: (deltaX, deltaY) => {
    set((state) => {
      const newX = state.viewportWorldPosition.x + deltaX
      const newY = state.viewportWorldPosition.y + deltaY
      console.log(`👆 [panViewport] Pan by (${deltaX.toFixed(0)}, ${deltaY.toFixed(0)}) → viewport at (${newX.toFixed(1)}, ${newY.toFixed(1)})`)
      return { 
        viewportWorldPosition: { x: newX, y: newY },
        viewportVersion: state.viewportVersion + 1
      }
    })
  },

  // Atomic update for zoom + viewport (prevents intermediate renders during zoom)
  setZoomAndViewport: (scale, x, y) => {
    const oldZoom = useAppStore.getState().zoomScale
    console.log(`🔍 [setZoomAndViewport] ZOOM: ${oldZoom.toFixed(2)}x → ${scale.toFixed(2)}x, viewport=(${x.toFixed(1)}, ${y.toFixed(1)})`)
    set((state) => ({ 
      zoomScale: scale,
      viewportWorldPosition: { x, y },
      viewportVersion: state.viewportVersion + 1
    }))
  },
  
  centerViewportOnFrame: (frameIndex, displayWidth?: number, displayHeight?: number, options?: { skipAutoSelect?: boolean }) => {
    set((state) => {
      const framePos = state.frameWorldPositions[frameIndex]
      
      if (!framePos) {
        const totalFrames = state.frameWorldPositions.length
        console.error(`❌ [centerViewportOnFrame] Invalid frame index: ${frameIndex} (valid range: 0-${totalFrames - 1})`)
        return {} // No state change
      }

      // Use provided display dimensions if available, fallback to camera intrinsics or viewport size
      let frameWidth = displayWidth
      let frameHeight = displayHeight
      
      if (!frameWidth || !frameHeight) {
        const camera = state.cameras[frameIndex]
        frameWidth = frameWidth || camera?.intrinsics?.width || state.viewportSize.width
        frameHeight = frameHeight || camera?.intrinsics?.height || state.viewportSize.height
      }

      // Holy red dot formula: viewport = frameCenter - screenCenter / zoom
      // This places the screen center (red dot) exactly on the frame center in world space
      const screenCenterX = state.viewportSize.width / 2
      const screenCenterY = state.viewportSize.height / 2
      const viewportX = framePos.worldX - screenCenterX / state.zoomScale
      const viewportY = framePos.worldY - screenCenterY / state.zoomScale
      
      const skipLabel = options?.skipAutoSelect ? ' [SKIP_AUTO_SELECT]' : ''
      console.log(`🎯 [centerViewportOnFrame] Centering holy red dot on frame ${frameIndex} at (${framePos.worldX.toFixed(1)}, ${framePos.worldY.toFixed(1)}) with zoom ${state.zoomScale.toFixed(2)}x${skipLabel}`)
      return { 
        viewportWorldPosition: { x: viewportX, y: viewportY },
        viewportVersion: state.viewportVersion + 1,
        skipAutoSelectNextViewportChange: options?.skipAutoSelect ? true : undefined
      }
    })
  },

  selectFrameWithNeighbors: (frameIndex, displayWidth, displayHeight) => {
    set((state) => {
      // Validate frame index
      if (frameIndex < 0 || frameIndex >= state.cameras.length) {
        console.error(`❌ [selectFrameWithNeighbors] Invalid frame index: ${frameIndex}`)
        return {}
      }

      const framePos = state.frameWorldPositions[frameIndex]
      if (!framePos) {
        console.error(`❌ [selectFrameWithNeighbors] No world position for frame ${frameIndex}`)
        return {}
      }

      // Use display dimensions if provided, fallback to intrinsic dimensions
      let frameWidth = state.viewportSize.width  // Default to viewport width
      let frameHeight = state.viewportSize.height // Default to viewport height
      
      if (displayWidth && displayHeight) {
        // Use provided display dimensions (from NewModeViewer)
        frameWidth = displayWidth
        frameHeight = displayHeight
      } else {
        // Fallback to intrinsic camera dimensions
        const camera = state.cameras[frameIndex]
        if (camera?.intrinsics) {
          frameWidth = camera.intrinsics.width || frameWidth
          frameHeight = camera.intrinsics.height || frameHeight
        }
      }
      
      // Holy red dot formula: viewport = frameCenter - screenCenter / zoom
      // This places the screen center (red dot) exactly on the frame center in world space
      const screenCenterX = state.viewportSize.width / 2
      const screenCenterY = state.viewportSize.height / 2
      const viewportX = framePos.worldX - screenCenterX / state.zoomScale
      const viewportY = framePos.worldY - screenCenterY / state.zoomScale

      // Build candidate list: selected frame + connected neighbors
      let candidateIndices = [frameIndex]
      
      if (state.connectivityGraph) {
        const connectedSet = state.connectivityGraph.get(frameIndex)
        if (connectedSet) {
          // Add connected frames to candidates
          const connectedArray = Array.from(connectedSet).sort((a, b) => a - b)
          candidateIndices = [frameIndex, ...connectedArray]
        }
      }

      // Set current camera and center viewport on frame center using holy red dot formula
      console.log(`✅ [selectFrameWithNeighbors] Selected frame ${frameIndex} at (${framePos.worldX.toFixed(1)}, ${framePos.worldY.toFixed(1)}) with zoom ${state.zoomScale.toFixed(2)}x, candidates: ${candidateIndices.length} frames`)
      return {
        currentCameraIndex: frameIndex,
        viewportWorldPosition: { x: viewportX, y: viewportY },
        viewportVersion: state.viewportVersion + 1,
        candidateFrameIndices: candidateIndices,
        manuallySelectedFrameIndex: frameIndex,
      }
    })
  },

  selectFrameWithoutPan: (frameIndex: number) => {
    set((state) => {
      // Validate frame index
      if (frameIndex < 0 || frameIndex >= state.cameras.length) {
        console.error(`❌ [selectFrameWithoutPan] Invalid frame index: ${frameIndex}`)
        return {}
      }

      // Build candidate list: selected frame + connected neighbors
      let candidateIndices = [frameIndex]
      
      if (state.connectivityGraph) {
        const connectedSet = state.connectivityGraph.get(frameIndex)
        if (connectedSet) {
          // Add connected frames to candidates
          const connectedArray = Array.from(connectedSet).sort((a, b) => a - b)
          candidateIndices = [frameIndex, ...connectedArray]
        }
      }

      // Calculate anchored positions for the newly selected frame
      // This re-anchors all frames relative to the selected frame
      let newAnchoredPositions = state.anchoredFramePositions
      if (state.offsetMap) {
        newAnchoredPositions = calculateAnchoredFramePositions(
          frameIndex,
          state.offsetMap,
          state.cameras.length,
          state.frameWorldPositions
        )
      }

      // 📍 Record navigation history (skip during playback and if frame unchanged)
      const oldFrame = state.manuallySelectedFrameIndex
      if (!state.isNavigatingHistory && oldFrame !== null && frameIndex !== oldFrame) {
        console.log(`📍 [selectFrameWithoutPan] Recording navigation: ${oldFrame} → ${frameIndex}`)
        // Need to call after state update, so use setTimeout
        setTimeout(() => {
          get().recordNavigationStep({
            startFrame: oldFrame,
            endFrame: frameIndex,
            timestamp: Date.now(),
            trigger: 'gesture',
            sectionId: get().currentSectionId
          })
        }, 0)
      }

      return {
        currentCameraIndex: frameIndex,
        manuallySelectedFrameIndex: frameIndex,
        candidateFrameIndices: candidateIndices,
        anchoredFramePositions: newAnchoredPositions,
        // Canvas stays fixed - no viewport change
        // Just change which frames are visible
        viewportVersion: state.viewportVersion + 1, // Force re-render
        lastManualSelectionTime: Date.now(), // Record when user manually selected
      }
    })
  },
  
  setViewportSize: (width, height) => {
    console.log(`📐 [setViewportSize] Resizing viewport to ${width}×${height}`)
    set((state) => ({ 
      viewportSize: { width, height },
      viewportVersion: state.viewportVersion + 1
    }))
  },
  
  // Legacy (DEPRECATED)
  setNextCameraIndex: (index) => {
    console.log('📸 [setNextCameraIndex] DEPRECATED - use setVisibleCameras instead. Next camera:', index)
    set({ nextCameraIndex: index })
  },
  
  setStitchOffset: (offsetX, offsetY) => {
    console.log('🔗 [setStitchOffset] DEPRECATED - use setVisibleCameras instead. Stitch offset:', { x: offsetX, y: offsetY })
    set({ stitchOffset: { x: offsetX, y: offsetY } })
  },
  
  // Frame Selection Tracking (Phase 2.8)
  recordFrameSelection: (selection) => {
    set((prevState) => ({
      previousFrameSelection: prevState.currentFrameSelection, // Store current as previous
      currentFrameSelection: selection, // Update to new selection
      frameSelectionHistory: [
        ...prevState.frameSelectionHistory.slice(-99), // Keep last 100 selections (limit memory)
        selection
      ]
    }))
    
    // Log selection for debugging
    console.log(`🏆 [recordFrameSelection] Frame #${selection.frameIndex} selected`, {
      algorithm: selection.algorithm,
      score: selection.score.toFixed(4),
      coverage: (selection.coverage * 100).toFixed(1) + '%',
      timestamp: new Date(selection.timestamp).toLocaleTimeString()
    })
  },
  
  toggleFrameSelectionBadge: () => {
    set((state) => {
      const newShow = !state.showFrameSelectionBadge
      console.log(`🎯 [toggleFrameSelectionBadge] Badge is now ${newShow ? 'visible' : 'hidden'}`)
      return { showFrameSelectionBadge: newShow }
    })
  },
  
  setShowFrameSelectionBadge: (show) => {
    console.log(`🎯 [setShowFrameSelectionBadge] Setting badge visibility to ${show}`)
    set({ showFrameSelectionBadge: show })
  },
  
  clearFrameSelectionHistory: () => {
    console.log('🗑️  [clearFrameSelectionHistory] Clearing selection history')
    set({ 
      frameSelectionHistory: [],
      currentFrameSelection: null,
      previousFrameSelection: null
    })
  },
  
  setCandidateFrames: (indices) => {
    console.log(`📋 [setCandidateFrames] Updated candidates: ${indices.length} frames`)
    set({ candidateFrameIndices: indices })
  },
  
  setManuallySelectedFrame: (frameIndex: number | null) => {
    const state = get()
    const oldFrame = state.manuallySelectedFrameIndex
    const jumpDistance = oldFrame !== null && frameIndex !== null ? Math.abs(frameIndex - oldFrame) : 0
    
    // 🔍 DEBUG: Log frame changes with stack trace
    if (frameIndex !== oldFrame) {
      const stackTrace = new Error().stack?.split('\n').slice(2, 6).join('\n') || 'No stack trace'
      console.log(`🎯 [setManuallySelectedFrame] ${oldFrame} → ${frameIndex} (distance: ${jumpDistance})`)
      console.log(`   📍 Called from:\n${stackTrace}`)
    }
    
    set({ manuallySelectedFrameIndex: frameIndex, lastManualSelectionTime: Date.now() })
    
    // 📍 Record navigation history
    if (!state.isNavigatingHistory && frameIndex !== null && frameIndex !== oldFrame) {
      // Record initial frame selection (oldFrame can be null)
      const startFrame = oldFrame !== null ? oldFrame : frameIndex
      console.log(`📍 [setManuallySelectedFrame] Recording navigation: ${startFrame} → ${frameIndex}`)
      get().recordNavigationStep({
        startFrame: startFrame,
        endFrame: frameIndex,
        timestamp: Date.now(),
        trigger: 'gesture',
        sectionId: state.currentSectionId
      })
    }
    // if (frameIndex !== null) {
    //   console.log(`🎯 [setManuallySelectedFrame] Frame #${frameIndex} manually selected - zoom preserved: ${oldZoom.toFixed(2)}x`)
    // } else {
    //   console.log(`🎯 [setManuallySelectedFrame] Cleared manual selection - zoom preserved: ${oldZoom.toFixed(2)}x`)
    // }
  },
  
  setAnimatingFrameIndex: (frameIndex: number | null) => {
    console.log(`🎬 [setAnimatingFrameIndex] Animation frame: ${frameIndex}`)
    set({ animatingFrameIndex: frameIndex })
  },
  
  setAnimationTrigger: (trigger: 'PAN_MOVE_PREVIEW' | 'PAN_END_SMOOTH' | 'DOUBLE_CLICK_ANIMATE' | 'SECTION_PLAY_ALL' | null) => {
    console.log(`🎬 [setAnimationTrigger] Trigger: ${trigger}`)
    set({ animationTrigger: trigger })
  },
  
  // 🎬 Frame sequence animation actions
  setFrameSequenceAnimating: (isAnimating: boolean) => {
    console.log(`🎬 [setFrameSequenceAnimating] ${isAnimating ? 'BLOCKING INPUT' : 'UNBLOCKING INPUT'}`)
    set({ isFrameSequenceAnimating: isAnimating })
  },
  
  setFrameSequenceAnimationCancel: (cancelFn: (() => void) | null) => {
    set({ frameSequenceAnimationCancel: cancelFn })
  },
  
  setFrameSequenceAnimationTarget: (targetFrame: number | null) => {
    set({ frameSequenceAnimationTarget: targetFrame })
  },
  
  cancelFrameSequenceAnimation: () => {
    const state = get()
    if (!state.isFrameSequenceAnimating) return
    
    console.log(`🎬 [cancelFrameSequenceAnimation] Stopping animation at frame ${state.animatingFrameIndex}`)
    
    // Cancel the animation service
    const cancelFn = state.frameSequenceAnimationCancel
    if (cancelFn) {
      cancelFn()
    }
    
    // Jump to current displaying frame (animatingFrameIndex)
    const currentFrame = state.animatingFrameIndex
    if (currentFrame !== null) {
      state.setManuallySelectedFrame(currentFrame)
      state.centerViewportOnFrame(currentFrame)
    }
    
    // Clear all animation state
    state.setFrameSequenceAnimating(false)
    state.setFrameSequenceAnimationCancel(null)
    state.setFrameSequenceAnimationTarget(null)
    state.setAnimatingFrameIndex(null)
    state.setAnimationTrigger(null)
  },
  
  // Camera trajectory overlay visibility
  showTrajectoryOverlay: () => {
    set({ trajectoryOverlayVisible: true })
  },
  
  setTrajectoryOverlayVisible: (visible: boolean) => {
    const state = get()
    
    // Clear existing timer
    if (state.trajectoryHideTimer) {
      clearTimeout(state.trajectoryHideTimer)
    }
    
    set({ trajectoryOverlayVisible: visible })
    
    // Start auto-hide timer when showing
    if (visible) {
      const timer = setTimeout(() => {
        set({ trajectoryOverlayVisible: false, trajectoryHideTimer: null })
      }, 5000) // 5 seconds
      
      set({ trajectoryHideTimer: timer })
    } else {
      set({ trajectoryHideTimer: null })
    }
  },
  
  setHighlightedZoneDirection: (direction: 'north' | 'south' | 'east' | 'west' | null) => {
    set({ highlightedZoneDirection: direction })
  },
  
  // 🎯 Overlay Persistence Actions (Feature 2: Unified Dismiss)
  // NOTE: Navigation hints are DECOUPLED - no setter actions needed
  setFrameBalloonVisible: (visible: boolean) => set({ frameBalloonVisible: visible }),
  setNavigationSettingsBalloonVisible: (visible: boolean) => set({ navigationSettingsBalloonVisible: visible }),
  
  dismissAllOverlays: () => {
    set({
      frameBalloonVisible: false,
      navigationSettingsBalloonVisible: false
      // hints remain always visible
    })
  },
  
  // 🎬 Play all frames in current section
  playAllFramesInSection: async () => {
    const state = get()
    
    // After loadSectionAsIndependentShop(), frames are available via cameras array
    const cameraCount = state.cameras.length
    
    if (cameraCount < 2) {
      console.warn(`🎬 [playAllFramesInSection] Cannot play: section has < 2 cameras`)
      return
    }
    
    // Build NSEW walkthrough path: tries east & west from frame 0, picks longer path
    const imageDimensions = state.imageDimensions
    if (!imageDimensions || state.frameWorldPositions.length === 0) {
      console.warn(`🎬 [playAllFramesInSection] Cannot build NSEW path: missing imageDimensions or frameWorldPositions`)
      return
    }
    
    const nsewPath = buildNSEWWalkthroughPath(
      0, // Start from frame 0
      state.frameWorldPositions,
      imageDimensions,
      60 // Max 60 steps
    )
    
    if (nsewPath.length < 2) {
      console.warn(`🎬 [playAllFramesInSection] NSEW path too short (${nsewPath.length}), skipping animation`)
      return
    }
    
    const targetFrame = nsewPath[nsewPath.length - 1]
    
    console.log(`🎬 [playAllFramesInSection] NSEW walkthrough: ${nsewPath.length} steps (frame ${nsewPath[0]} → ${targetFrame})`)
    
    // Get animation service and config
    const animationService = getDoubleClickAnimationService()
    const config = getAnimationConfig('DOUBLE_CLICK_ANIMATE')
    
    if (!config) {
      console.warn(`🎬 [playAllFramesInSection] No animation config, jumping to last frame`)
      state.setManuallySelectedFrame(targetFrame)
      state.centerViewportOnFrame(targetFrame)
      return
    }
    
    // Slow entry animation: 1000ms per step with cross-fade
    const dynamicConfig = {
      ...config,
      frameDuration: 1000,
    }
    
    // Preload the first few frames of the path before starting
    const shopId = state.currentShopId || ''
    const sectionPath = state.currentSectionFramesPath
    const preloadCount = Math.min(5, nsewPath.length)
    const preloadPromises: Promise<unknown>[] = []
    
    for (let i = 0; i < preloadCount; i++) {
      const camera = state.cameras[nsewPath[i]]
      if (camera) {
        const url = getFrameImagePath(shopId, camera.frame_file, sectionPath)
        preloadPromises.push(imageCache.preload(url).catch(() => {}))
      }
    }
    
    // Poll for first frame to be cached (every 100ms, max 5 seconds)
    let pollCount = 0
    const maxPolls = 50
    
    const pollIntervalId = setInterval(() => {
      pollCount++
      const freshState = get()
      
      const camera = freshState.cameras[nsewPath[0]]
      if (!camera) {
        console.warn(`🚫 [playAllFramesInSection] Frame ${nsewPath[0]} camera not found, skipping animation`)
        clearInterval(pollIntervalId)
        return
      }
      
      const imagePath = getFrameImagePath(shopId, camera.frame_file, sectionPath)
      const isReady = imageCache.has(imagePath)
      
      if (isReady || pollCount >= maxPolls) {
        clearInterval(pollIntervalId)
        
        if (!isReady) {
          console.warn(`🚫 [playAllFramesInSection] Frame ${nsewPath[0]} not cached after ${maxPolls * 100}ms, skipping animation`)
          return
        }
        
        console.log(`✅ [playAllFramesInSection] Frame ${nsewPath[0]} ready after ${pollCount * 100}ms, starting NSEW walkthrough...`)
        
        // Record start frame in history
        const startFrame = nsewPath[0]
        freshState.recordNavigationStep({
          startFrame: startFrame,
          endFrame: startFrame,
          timestamp: Date.now(),
          trigger: 'gesture',
          sectionId: freshState.currentSectionId
        })
        
        // Set animation blocking state
        freshState.setFrameSequenceAnimating(true)
        freshState.setFrameSequenceAnimationTarget(targetFrame)
        freshState.setAnimatingFrameIndex(nsewPath[0])
        freshState.setAnimationTrigger('SECTION_PLAY_ALL')
        
        // Preload remaining path frames in background during the slow animation
        for (let i = preloadCount; i < nsewPath.length; i++) {
          const cam = freshState.cameras[nsewPath[i]]
          if (cam) {
            const url = getFrameImagePath(shopId, cam.frame_file, sectionPath)
            imageCache.preload(url).catch(() => {})
          }
        }
        
        // Start the NSEW walkthrough animation
        const cancelFn = animationService.animateFrameSequence(
          nsewPath,
          targetFrame,
          dynamicConfig,
          {
            onFrameShow: (frameIdx: number) => {
              const s = useAppStore.getState()
              s.setAnimatingFrameIndex(frameIdx)
              s.centerViewportOnFrame(frameIdx, undefined, undefined, { skipAutoSelect: true })
            },
            onComplete: (finalFrame: number) => {
              console.log(`✅ [playAllFramesInSection] NSEW walkthrough complete at frame: ${finalFrame}`)
              const s = useAppStore.getState()
              
              s.setAnimatingFrameIndex(null)
              s.setAnimationTrigger(null)
              s.setFrameSequenceAnimating(false)
              s.setFrameSequenceAnimationCancel(null)
              s.setFrameSequenceAnimationTarget(null)
              
              s.setManuallySelectedFrame(finalFrame)
              s.centerViewportOnFrame(finalFrame)
            }
          }
        )
        
        freshState.setFrameSequenceAnimationCancel(cancelFn)
      }
    }, 100)
  },
  
  // Frame Navigation Strategy actions
  initializeFrameNavigationStrategy: (frameWidth: number, frameHeight: number) => {
    const state = get()
    
    if (!state.connectivityGraph || !state.offsetMap) {
      console.warn('❌ [initializeFrameNavigationStrategy] Cannot initialize: missing connectivityGraph or offsetMap')
      return
    }
    
    // Initialize Direct Hit strategy
    const strategy = new DirectHitStrategy(
      state.connectivityGraph,
      state.offsetMap,
      {
        x: 0.5, // 50% of viewport (will be set properly in component)
        y: 0.4, // 40% of viewport
        width: frameWidth,
        height: frameHeight
      }
    )
    
    console.log(`✅ [initializeFrameNavigationStrategy] Direct Hit strategy initialized (frame: ${frameWidth}x${frameHeight})`)
    set({
      frameNavigationStrategy: strategy,
      originBoundarySize: { width: frameWidth, height: frameHeight }
    })
  },
  
  updateFrameNavigation: (deltaPanX: number, deltaPanY: number, panDirection: 'up' | 'down' | 'left' | 'right' | null) => {
    const state = get()
    const strategy = state.frameNavigationStrategy as DirectHitStrategy | null
    
    if (!strategy) {
      console.warn('❌ [updateFrameNavigation] Strategy not initialized')
      return
    }
    
    const currentFrame = state.manuallySelectedFrameIndex ?? state.currentCameraIndex
    const { width: frameWidth, height: frameHeight } = state.originBoundarySize
    
    const result = strategy.selectNextFrame(
      currentFrame,
      deltaPanX,
      deltaPanY,
      panDirection,
      frameWidth,
      frameHeight
    )
    
    console.log(`🧭 [updateFrameNavigation] ${result.reason} (selected: ${result.selectedFrame}, changed: ${result.hasChanged})`)
    
    set({ lastNavigationResult: result })
    
    // If selection changed, update manually selected frame
    if (result.hasChanged && result.selectedFrame !== currentFrame) {
      state.setManuallySelectedFrame(result.selectedFrame)
    }
  },
  
  setPanFailureDirection: (direction: 'up' | 'down' | 'left' | 'right' | null) => {
    set({ panFailureDirection: direction })
  },
  
  setAvailableDirections: (directions: ('north' | 'south' | 'east' | 'west')[]) => {
    set({ availableDirections: directions })
  },

  setArrowCandidateFrames: (frames: number[]) => {
    set({ arrowCandidateFrames: frames })
  },

  setOrbitSequence: (sequence: OrbitEntry[] | null, tappedDepth?: number, point3D?: [number, number, number] | null, circleRadiusMeters?: number) => {
    set({ orbitSequence: sequence, orbitIndex: 0, orbitTappedDepth: tappedDepth ?? 1.0, orbitPoint3D: point3D ?? null, orbitCircleRadiusMeters: circleRadiusMeters ?? 0 })
  },

  setOrbitIndex: (index: number) => {
    set({ orbitIndex: index })
  },

  clearOrbit: () => {
    set({ orbitSequence: null, orbitIndex: 0, orbitTappedDepth: 1.0, orbitPoint3D: null, orbitCircleRadiusMeters: 0 })
  },
  
  resetFrameNavigationStrategy: () => {
    const state = get()
    const strategy = state.frameNavigationStrategy as DirectHitStrategy | null
    
    if (strategy) {
      strategy.resetState()
      console.log('🔄 [resetFrameNavigationStrategy] Strategy state reset')
    }
    
    set({ lastNavigationResult: null })
  },
  
  resetPanZoom: () => {
    console.log('🔄 [resetPanZoom] Resetting to defaults')
    set({ 
      panOffset: { x: 0, y: 0 }, 
      zoomScale: 1.0,
      isPanning: false,
      visibleCameraIndices: [0], // Reset to first frame only
      visibleCameraOffsets: new Map([[0, { x: 0, y: 0 }]]), // Frame 0 at origin
      // Legacy
      nextCameraIndex: null,
      stitchOffset: { x: 0, y: 0 }
    })
  },
  
  reset: () => set({
    currentShopId: null,
    cameras: [],
    points3D: [],
    isLoadingData: false,
    dataError: null,
    currentCameraIndex: 0,
    isTransitioning: false,
    panOffset: { x: 0, y: 0 },
    zoomScale: 1.0,
    isPanning: false,
    visibleCameraIndices: [0],
    visibleCameraOffsets: new Map([[0, { x: 0, y: 0 }]]),
    nextCameraIndex: null,
    stitchOffset: { x: 0, y: 0 },
    menuOpen: false,
    showDebugOverlay: false,
    showMeshViewer: false,
    currentMeshSection: 0,
    settings: defaultSettings,
  }),
  
  // Load a section as an independent shop
  // Each section folder is treated as a complete self-contained shop
  // INTERNAL: Shared data loading utility (Phase 1: Code Unification)
  // Encapsulates all common logic between loadSectionAsIndependentShop() and switchToSection()
  // This eliminates ~150 lines of duplication while maintaining both function signatures
  _loadSectionData: async (sectionPaths: {
    cameras: string
    points3d: string
    offset_map: string
    frames: string
  }, sectionId: number) => {
    const camerasPath = sectionPaths.cameras
    const pointsPath = sectionPaths.points3d
    const offsetMapPath = sectionPaths.offset_map
    const framesPath = sectionPaths.frames
    
    // Build manifest path from offset_map path
    const reconstructionDir = offsetMapPath.substring(0, offsetMapPath.lastIndexOf('/'))
    const section3dPath = `${reconstructionDir}/section_3d.json`
    const manifestPath = `${reconstructionDir}/manifest.json`
    
    // Try loading unified section_3d.json.gz first (NEW - saves 12.59 MB per section!)
    let camerasData = null
    let pointsData = null
    let manifestData = null
    let offsetMapData = null
    
    try {
      console.log(`[*] Attempting to load section_3d.json.gz (unified format)...`)
      const section3dResponse = await loadSection3D(section3dPath)
      
      // Convert to expected format
      camerasData = { cameras: section3dResponse.cameras }
      pointsData = { points: section3dResponse.points3D }
      
      console.log(`[OK] Loaded section_3d.json.gz: ${section3dResponse.cameras.length} cameras, ${section3dResponse.points3D.length} points (UNIFIED FORMAT)`)
      // Note: Continue to manifest loading for world positions and connectivity (existing workflow)
    } catch (error) {
      console.warn(`[!] section_3d.json.gz not available, falling back to separate cameras/points:`, (error as Error).message)
      
      // FALLBACK: Load cameras and points3D separately (legacy format)
      const [camerasResponse, pointsResponse] = await Promise.all([
        fetch(toBlobUrl(camerasPath)),
        fetch(toBlobUrl(pointsPath))
      ])
      
      if (!camerasResponse.ok) throw new Error(`Failed to load cameras for section ${sectionId}: ${camerasResponse.status}`)
      if (!pointsResponse.ok) throw new Error(`Failed to load points3D for section ${sectionId}: ${pointsResponse.status}`)
      
      camerasData = await camerasResponse.json()
      pointsData = await pointsResponse.json()
    }
    
    // Load manifest as PRIMARY source for connectivity (fallback to offset_map if manifest unavailable)
    manifestData = await loadManifestWithFallback(manifestPath)
    if (manifestData) {
      try {
        const frameCount = manifestData.frames?.length || 0
        const avgNeighbors = frameCount > 0 ? (Object.values(manifestData.frames).reduce((sum: number, f: any) => sum + Object.keys(f.offsets).length, 0) / frameCount).toFixed(0) : 0
        console.log(`[_loadSectionData] Manifest loaded: ${frameCount} frames (avg ${avgNeighbors} neighbors)`)
      } catch (e) {
        console.warn(`[_loadSectionData] Failed to parse manifest.json, will load offset_map as fallback:`, e)
        manifestData = null
      }
    }
    
    // Only load offset_map if manifest failed (FALLBACK only)
    if (!manifestData) {
      console.log(`[_loadSectionData] Loading offset_map.json as fallback...`)
      const offsetMapResponse = await fetch(toBlobUrl(offsetMapPath))
      if (offsetMapResponse.ok) {
        offsetMapData = await offsetMapResponse.json() as OffsetMap
        console.log(`[_loadSectionData] Offset_map loaded (FALLBACK)`)
      } else {
        console.warn(`[_loadSectionData] Neither manifest nor offset_map available for section ${sectionId}`)
      }
    } else {
      // Manifest available - build synthetic offsetMap (saves 2-3MB per section)
      offsetMapData = buildSyntheticOffsetMapFromManifest(manifestData)
      console.log(`[_loadSectionData] Built synthetic offsetMap from manifest (saves ~2-3MB per section)`)
    }
    
    // Build frame world positions (prefer manifest, fallback to offset_map)
    let worldPositions: Array<{ frameIndex: number; worldX: number; worldY: number }> = []
    const frameWidth = camerasData.cameras?.[0]?.intrinsics?.width || 1920
    const frameHeight = camerasData.cameras?.[0]?.intrinsics?.height || 1080
    
    if (manifestData && camerasData.cameras?.length) {
      worldPositions = calculateFrameWorldPositions(null as any, camerasData.cameras.length, manifestData, frameWidth, frameHeight)
      console.log(`[_loadSectionData] World positions calculated from manifest`)
    } else if (offsetMapData && camerasData.cameras?.length) {
      worldPositions = calculateFrameWorldPositions(offsetMapData, camerasData.cameras.length, undefined, frameWidth, frameHeight)
      console.log(`[_loadSectionData] World positions calculated from offset_map (fallback)`)
    } else {
      worldPositions = camerasData.cameras?.map((_: any, i: number) => ({
        frameIndex: i,
        worldX: i * 50,
        worldY: 0
      })) || []
      console.warn(`[_loadSectionData] World positions using grid layout (no manifest or offset_map)`)
    }
    
    // Build spatial structures
    const pointCloudMappings = buildPointCloudMappings(camerasData.cameras, pointsData?.points || [])
    const pointKDTree = createPointKDTree(pointsData?.points || [])
    const cameraKDTree = createCameraKDTree(camerasData.cameras)
    
    // Build connectivity graph (prefer manifest, fallback to offset_map)
    let connectivityGraphData = null
    if (manifestData) {
      connectivityGraphData = buildConnectivityGraphFromManifest(manifestData)
      console.log(`[_loadSectionData] Connectivity graph from manifest`)
    } else if (offsetMapData) {
      connectivityGraphData = buildConnectivityGraph(offsetMapData, camerasData.cameras?.length || 0)
      console.log(`[_loadSectionData] Connectivity graph from offset_map (fallback)`)
    } else {
      console.warn(`[_loadSectionData] No connectivity graph available`)
    }
    
    return {
      cameras: camerasData.cameras || [],
      points: pointsData?.points || [],
      offsetMap: offsetMapData,
      manifest: manifestData,
      worldPositions,
      pointCloudMappings,
      pointKDTree,
      cameraKDTree,
      connectivityGraph: connectivityGraphData,
      framesPath
    }
  },
  
  loadSectionAsIndependentShop: async (shopId: string, sectionId: number) => {
    console.log(`📂 [loadSectionAsIndependentShop] Loading section ${sectionId} from ${shopId} as independent shop`)
    
    try {
      set({ isLoadingData: true, dataError: null, loadingProgress: 0 })
      
      // Get base path from dataset configuration to support folder organization (e.g., NewShops/)
      const state = get()
      const dataset = state.availableDatasets.find(d => d.id === shopId)
      const datasetBasePath = dataset?.paths?.metadata?.replace('/shop_metadata.json', '') || `/api/scan-data/${shopId}`
      
      // Build paths from shopId/sectionId
      const sectionPath = `${datasetBasePath}/section_${sectionId}`
      
      const camerasPath = `${sectionPath}/cameras.json`
      const pointsPath = `${sectionPath}/points3d.json`
      const offsetMapPath = `${sectionPath}/offset_map.json`
      const framesPath = `${sectionPath}/frames`
      
      const sectionPaths = {
        cameras: camerasPath,
        points3d: pointsPath,
        offset_map: offsetMapPath,
        frames: framesPath
      }
      
      console.log(`📂 [loadSectionAsIndependentShop] Section paths:`, { camerasPath, framesPath })
      set({ loadingProgress: 10 })
      
      // Load section data via shared utility
      const sectionData = await get()._loadSectionData(sectionPaths, sectionId)
      set({ loadingProgress: 70 })
      
      console.log(`✅ [loadSectionAsIndependentShop] Loaded ${sectionData.cameras?.length || 0} cameras for section ${sectionId}`)
      
      // Calculate initial viewport position
      let initialViewportPos = { x: 0, y: 0 }
      if (sectionData.worldPositions.length > 0) {
        const firstFramePos = sectionData.worldPositions[0]
        initialViewportPos = { x: firstFramePos.worldX, y: firstFramePos.worldY }
      }
      
      // Update store with this section's data
      set({
        cameras: sectionData.cameras,
        frameWorldPositions: sectionData.worldPositions,
        pointCloudMappings: sectionData.pointCloudMappings,
        pointKDTree: sectionData.pointKDTree,
        cameraKDTree: sectionData.cameraKDTree,
        offsetMap: sectionData.offsetMap,
        connectivityGraph: sectionData.connectivityGraph,
        points3D: sectionData.points,
        currentSectionId: sectionId,
        currentCameraIndex: 0,
        manuallySelectedFrameIndex: 0,
        currentSectionFramesPath: sectionPaths.frames,
        canUseTapIndicator: true, // ✅ Enable tap indicator (offsetMap/manifest loaded)
        isLoadingData: false,
        dataError: null,
        loadingProgress: 100,
        // RESET CANVAS STATE - Center on first frame
        panOffset: { x: 0, y: 0 },
        zoomScale: 1.0,
        viewportWorldPosition: initialViewportPos,
        isPanning: false,
        isZooming: false,
      })
      
      console.log(`✅ [loadSectionAsIndependentShop] Store updated (section ${sectionId}, ${sectionData.cameras.length} cameras)`)
      
      // Auto-zoom to first frame
      if (sectionData.worldPositions.length > 0 && sectionData.cameras?.length > 0) {
        const state = get()
        const viewportWidth = state.viewportSize.width
        const viewportHeight = state.viewportSize.height
        const firstCamera = sectionData.cameras[0]
        const frameHeight = firstCamera.intrinsics?.height || 1080
        const zoomY = viewportHeight / frameHeight
        const fitZoom = zoomY
        const firstFrameWorldPos = sectionData.worldPositions[0]
        const viewportX = firstFrameWorldPos.worldX - (viewportWidth / 2) / fitZoom
        const viewportY = firstFrameWorldPos.worldY - (viewportHeight / 2) / fitZoom
        
        set({
          zoomScale: fitZoom,
          viewportWorldPosition: { x: viewportX, y: viewportY }
        })
      }
      
    } catch (error) {
      console.error(`❌ [loadSectionAsIndependentShop] Error loading section ${sectionId}:`, error)
      set({ 
        isLoadingData: false, 
        dataError: `Failed to load section ${sectionId}: ${error instanceof Error ? error.message : 'Unknown error'}` 
      })
    }
  },
  
  // INTERMEDIATE: Multi-Section Support
  switchToSection: async (sectionId: number, options?: { skipAnimation?: boolean }) => {
    const state = get()
    
    // Guard: Skip if already loaded and current AND has actual data
    if (state.currentSectionId === sectionId && state.loadedSectionIds.has(sectionId) && state.cameras.length > 0) {
      console.log(`✅ [switchToSection] Section ${sectionId} already loaded, skipping reload`)
      set({ currentSectionId: sectionId })
      return
    }
    
    const shopMetadata = state.shopMetadata
    
    if (!shopMetadata || !state.isMultiSection) {
      console.warn(`[switchToSection] Not a multi-section shop`)
      return
    }
    
    // Find section metadata by ID
    const sectionMetadata = shopMetadata.sections.find(s => s.section_id === sectionId)
    if (!sectionMetadata) {
      console.warn(`[switchToSection] Section ${sectionId} metadata not found`)
      return
    }
    
    console.log(`\n${'='.repeat(70)}`)
    console.log(`🔄 [switchToSection] SWITCHING TO SECTION ${sectionId}`)
    console.log(`${'='.repeat(70)}`)
    
    console.log(`\n📊 BEFORE SWITCH: Section ID ${state.currentSectionId}, ${state.cameras.length} cameras, ${state.points3D.length} points`)
    console.log(`📋 TARGET: Section ${sectionId} (${sectionMetadata.name}) - ${sectionMetadata.camera_count} cameras, ${sectionMetadata.point_count} points`)
    
    try {
      console.log(`\n🔄 Loading section ${sectionId} data from shopMetadata paths...`)
      
      // Load section data via shared utility
      const sectionData = await get()._loadSectionData(sectionMetadata.paths, sectionId)
      
      console.log(`\n✅ LOADED SECTION ${sectionId}:`)
      console.log(`   Cameras: ${sectionData.cameras.length}, Points3D: ${sectionData.points.length}`)
      console.log(`   World Positions: ${sectionData.worldPositions.length}, Connectivity edges: ${sectionData.connectivityGraph ? 'yes' : 'no'}`)
      
      // Update store with this section's data (includes animation trigger)
      set({
        cameras: sectionData.cameras,
        points3D: sectionData.points,
        frameWorldPositions: sectionData.worldPositions,
        pointCloudMappings: sectionData.pointCloudMappings,
        pointKDTree: sectionData.pointKDTree,
        cameraKDTree: sectionData.cameraKDTree,
        offsetMap: sectionData.offsetMap,
        connectivityGraph: sectionData.connectivityGraph,
        currentSectionId: sectionId,
        currentCameraIndex: 0,
        manuallySelectedFrameIndex: 0,
        currentSectionFramesPath: sectionMetadata.paths.frames,
        canUseTapIndicator: true, // ✅ Enable tap indicator
        // Populate sections for drawer
        sections: shopMetadata.sections.map((s) => {
          const [startIdx, endIdx] = s.frame_range
          const frames = Array.from({ length: s.frame_count }, (_, i) => startIdx + i)
          return {
            sectionId: s.section_id,
            startFrame: startIdx,
            endFrame: endIdx,
            frameCount: s.frame_count,
            frames
          }
        })
      })
      
      console.log(`✅ [switchToSection] Section ${sectionId} loaded, triggering animation...`)
      
      // Auto-zoom to first frame
      if (sectionData.worldPositions.length > 0 && sectionData.cameras?.length > 0) {
        const framePos = sectionData.worldPositions[0]
        const viewportWidth = state.viewportSize.width
        const viewportHeight = state.viewportSize.height
        const frameHeight = sectionData.cameras[0].intrinsics?.height || 1080
        const zoomY = viewportHeight / frameHeight
        const fitZoom = zoomY
        
        set({
          zoomScale: fitZoom,
          viewportWorldPosition: {
            x: framePos.worldX - (viewportWidth / 2) / fitZoom,
            y: framePos.worldY - (viewportHeight / 2) / fitZoom
          }
        })
      }
      
      console.log(`${'='.repeat(70)}\n`)
      
      // 🎬 TRIGGER ANIMATION (unless skipped by URL navigation)
      if (!options?.skipAnimation) {
        get().playAllFramesInSection()
      } else {
        console.log(`⏭️ [switchToSection] Skipping animation (URL navigation)`)
      }
      
    } catch (error) {
      console.error(`\n❌ [switchToSection] Error loading section ${sectionId}:`, error)
      set({ 
        dataError: `Failed to load section ${sectionId}: ${error instanceof Error ? error.message : 'Unknown error'}` 
      })
      console.log(`${'='.repeat(70)}\n`)
    }
  },
  
  // Image dimensions for navigation logic
  setImageDimensions: (dimensions) => {
    console.log(`📐 [setImageDimensions] Setting image dimensions:`, dimensions)
    set({ imageDimensions: dimensions })
  },
  
  // Toggle sections drawer visibility
  toggleSectionsDrawer: () => {
    set((state) => ({
      isDrawerOpen: !state.isDrawerOpen,
    }))
  },

  // Load a different user section's dataset (cross-section navigation in drawer)
  //
  // `options` is forwarded to loadDataset rather than dropped: a doorway transition needs
  // `skipAnimation` so the section it lands in does not immediately seize the camera for a
  // 60-step auto-walkthrough, and a future authored link needs `frame` to land on the frame
  // that actually faces back through the door.
  loadSectionDataset: async (userSectionId: string, options) => {
    const state = get()
    const targetDataset = state.shopDatasets.find(d => d.sectionId === userSectionId)
    if (!targetDataset) {
      console.error(`❌ [loadSectionDataset] No dataset found for section ${userSectionId}`)
      return
    }
    if (targetDataset.id === state.currentDatasetId) {
      console.log(`ℹ️ [loadSectionDataset] Already loaded: ${userSectionId}`)
      return
    }
    console.log(`🔄 [loadSectionDataset] Switching to section "${targetDataset.sectionName}" (dataset ${targetDataset.id})`)
    await get().loadDataset(targetDataset.id, options)
  },
}))

// Expose store on window for E2E tests
if (typeof window !== 'undefined') {
  ;(window as any).__appStore = useAppStore
}
